/**
 * The game loop and system wiring.
 *
 * Three clocks, deliberately:
 *   - **render**: whatever rAF gives us
 *   - **simulation**: fixed 60 Hz — cell movement, reactions, thermal, charge
 *   - **movement**: fixed 120 Hz — the player controller
 *
 * Movement runs at double the sim rate for the reasons in docs/02-movement.md
 * §1: halved sweep length, and buffered inputs consumed on the next substep
 * rather than the next frame.
 *
 * The accumulator is clamped. A tab that was backgrounded for 30 seconds must
 * not try to catch up 1800 ticks — it drops them, because a spiral of death is
 * worse than a discontinuity nobody was watching.
 */

import { Input } from './input';
import { profiler } from './profiler';
import { Rng } from './rng';
import { CellWorld, CHUNK_SHIFT } from '../sim/world';
import { CellSim } from '../sim/cellsim';
import { Thermal } from '../sim/thermal';
import { Charge } from '../sim/charge';
import { Pressure } from '../sim/pressure';
import { Particles } from '../sim/particles';
import { ReactionEngine } from '../sim/reactions';
import { Supports } from '../sim/supports';
import { AttractField } from '../sim/attract';
import { RigidBodies } from '../sim/rigid';
import { withSupportSites } from '../world/supportSites';
import { RunBodies, withCrateSites, withDoorSites } from '../world/bodySites';
import { RunTerrain, withRunDiffs } from '../world/runTerrain';
import { SurfaceTerrain, withSurfaceDiffs } from '../world/surfaceTerrain';
import { riteFor } from '../entity/deathRite';
import { createGatherState, stepGather, type GatherCtx, type GatherState } from '../combat/gather';
import { LightField } from '../sim/light';
import { buildReactionRules } from '../sim/reactionData';
import { MAT, registerMaterials } from '../sim/materialData';
import { M, materialDef } from '../sim/materials';
import { testRoomGenerator, testRoomSpawn } from '../sim/testRoom';
import {
  Comp, DAMAGE_TYPE_NAMES, DamageType, Team, World, standingHalfH, type Entity,
} from '../entity/ecs';
import {
  DEEP_WATER_SUBMERSION, createMover, setStance, stepMover, type MoveContext, type MoverState,
} from '../entity/movement';
import { applyModifiers, defaultTuning } from '../entity/movementTuning';
import { displaceCells } from '../entity/body';
import { GLContext } from '../render/gl';
import { Camera } from '../render/camera';
import { TerrainRenderer } from '../render/terrain';
import { LightTexture } from '../render/light';
import { Shape, SpriteBatch } from '../render/sprites';
import { PixelBodyRenderer } from '../render/pixelBodies';
import { BackdropRenderer } from '../render/backdrop';
import { dayPhase } from '../render/dayNight';
import { PixelBodySystem, planNameFor } from '../entity/pixelBodySystem';
import { bodyQuad } from '../entity/pixelBody';
import { applyDrowning, clearStatuses } from '../combat/status';
import { WorldGen, MODE_COHESION } from '../world/worldgen';
import { biomeById, buildBiomes } from '../world/biomes';
import { resolveWorldModifiers } from '../meta/worldModifiers';
import { buildWeapons } from '../combat/weaponData';
import { buildModules } from '../combat/moduleData';
import type { Hand, ModuleDef, WeaponDef, WeaponInstance } from '../combat/types';
import {
  attackDragOf, createWielder, equip, isAttacking, isStaggered, nodeDuration,
  nodeWindup, takeSwingStart,
  drawnSwingAngle, refresh, rollWeapon, startParry, statsForLevel, swingAimRotation,
  swingSpine, updateWielder,
  type WielderCtx, type WielderState,
} from '../combat/wielder';
import { Ability, pogo, refreshOnHitConfirm, type CombatMoveState } from '../entity/movement';
import {
  TETHER_ROPE_SEGMENTS, tetherDrawAngle, tetherRope, updateAbilities, type AbilityCtx,
} from '../entity/abilities';
import { Projectiles, makeFireHandler } from '../combat/projectiles';
import type { HitResult } from '../combat/resolve';
import { Summons, makeSummonHandler } from '../combat/summons';
import { spawnFlockExtras, stepPlayerFlock } from '../combat/playerFlock';
import { dropSourceFor, rollModuleDrop } from '../combat/moduleDrops';
import { makeEnemyAttacks } from '../entity/ai/enemyAttacks';

/**
 * Traversal ability id (meta's vocabulary) → controller bitmask. The two layers
 * name the same nine abilities and must not import each other, so the mapping
 * is stated once, here, where they meet.
 */
// Damage type → blade material now lives in `weaponVisual.ts`, with the
// legibility rule that picks it: see `bladeMatForDamage`.

const ABILITY_BY_ID: Record<string, number> = {
  airstep: Ability.AIRSTEP,
  cling: Ability.CLING,
  tether: Ability.TETHER,
  breach: Ability.BREACH,
  blink: Ability.BLINK,
  burrow: Ability.BURROW,
  'fluid-adapt': Ability.FLUID_ADAPT,
  'phase-drift': Ability.PHASE_DRIFT,
  updraft: Ability.UPDRAFT,
};
import { EnemyRegistry, type BiomeId } from '../entity/enemyData';
import { EnemySpawner, bodyFitsAt } from '../entity/spawner';
import { stepEnemyBodies, updateEnemies, type EnemyAiCtx } from '../entity/ai/enemyAI';
import { setNavStructure } from '../entity/ai/pathing';
import { UIRoot } from '../ui/uiRoot';
import type { ToastKind } from '../ui/viewModels';
import { UIAdapter, buildGameOverVM, type UIVendorInput } from './uiAdapter';
import { getArchetype, resolveMovement, resolveStance, type DamageModel } from '../meta/archetypes';
import { bodyForArchetype, compositionIds } from '../entity/archetypeBody';
import {
  MIN_HALF_WIDTH,
  MIN_ROUND_RADIUS,
  WEAPON_GLOW,
  bladeMatForDamage,
  makeWeaponStamp,
  profileAt,
  roundProfileAt,
  roundRunLength,
  roundStamps,
  stampAt,
  stampSteps,
  visualForClass,
  type WeaponVisual,
} from '../entity/weaponVisual';
import { labWheelDefs } from './labBench';
import { WeaponAltar, CLAIM_RANGE } from '../world/weaponAltar';
import { TREE_HOLLOW_SITE } from '../world/greatTree';
import { desertCacheSite } from '../world/mountain';
import {
  ABILITY_TEXT, buildCache, inRange, nextAbility, type AbilityCache,
} from '../world/abilityCache';
import {
  drinkPool, newVisit, sanctumContaining, updateVisit, type SanctumVisit,
} from '../world/sanctum';
import { unlockedModuleIds, unlockedWeaponIds } from '../meta/unlockEffects';
import { Progression } from '../meta/progression';
import {
  abilities as runAbilities, addModule, addWeapon, advanceRun, bankRun, causeHeadline, causeSentence,
  createRunState, endRun,
  enterBiome, enterRoom, grantAbility, recordDiscovery, recordKill, refillRoomRevival,
  type DeathCause, type RunOutcomeKind, type RunState,
} from '../meta/runState';
import { getFaction, opposedOf } from '../meta/factions';
import { SaveSystem, createProfile, type ProfileData } from '../meta/save';
import { getKit, type KitDef } from '../meta/kits';
import { Stash } from '../meta/stash';
import { craft, craftingVM } from '../meta/crafting';
import { redactEnemyEntry, reactionHint, resolvePassives, type RunPassives } from '../meta/passives';
import { restNodeAt, restServices, type RestNode } from '../world/restNode';
import { ambientAt, setSurfaceAmbientMul, setUndergroundAmbientMul } from '../world/lighting';
import { surfaceDressing } from '../world/surfaceDressing';
import { undergroundDressing } from '../world/undergroundDressing';
import {
  PROLOGUE_AMBIENT, PROLOGUE_SPAWN, prologueGenerator, prologueMarkAt,
} from '../world/prologue';
import {
  PROLOGUE_BEAT_IDS, PROLOGUE_END_BEAT, PROLOGUE_SKIP_HOLD_MS, prologueVM,
} from '../ui/prologue';
import { FactionVendor, vendorForRestNode, type AmbushRequest } from '../world/vendor';
import { describeGrants, resolveGrants } from './unlockApply';

const SIM_HZ = 60;
const SIM_DT = 1 / SIM_HZ;
const MOVE_HZ = 120;
const MOVE_DT = 1 / MOVE_HZ;
/** Never simulate more than this much wall time in one frame. */
const MAX_CATCHUP = 0.25;

/**
 * The damage at which a blow reads as *full force*, for everything above
 * combat that wants a 0..1 out of a damage number.
 *
 * One number and one expression (`blowSeverity`) rather than two, because the
 * two readers were already disagreeing about whether force is a thing
 * presentation knows: `presentHit` scaled the wound spray by `damage / 40`
 * while the enemy-attack hook shook the camera by a flat 0.08 for a 6-damage
 * skitter bite and a 20-damage boss slam alike (#141). Sharing the ramp is what
 * makes "how hard was that" one answer instead of one answer and one constant.
 */
const FULL_FORCE_DAMAGE = 40;

/** How hard a blow landed, 0..1. See `FULL_FORCE_DAMAGE`. */
function blowSeverity(damage: number): number {
  return Math.min(1, Math.max(0, damage) / FULL_FORCE_DAMAGE);
}

/**
 * The three things an enemy blow can do to the player, and what each one looks
 * like. Kept together because their one hard requirement is *relative*: a
 * player must be able to tell which of the three happened (#141), and numbers
 * that only make sense next to each other should be read next to each other.
 *
 * The channels differ, not just the magnitudes, so the distinction survives a
 * player who is not counting pixels:
 *
 *   - **A hit** flinches the body that took it and rattles the viewpoint, from
 *     a floor plus `blowSeverity`. It is the only one of the three that poses
 *     the *target*.
 *   - **A parry** flinches the **attacker** — `tryParryWindow` has just stamped
 *     it with the parry's primer (`STAGGERED` on all three parry weapons) and
 *     cancelled its blow, so the recoil is the attacker's own state and not a
 *     borrowed hurt pose — and rattles the viewpoint sharply and briefly.
 *
 *     No *defender* posture is drawn, and that is a decision rather than an
 *     omission. `pixelBody.ts` has no guard or deflect posture — `Pose` is
 *     IDLE / RUN / AIR / ATTACK / HURT / DEAD — so a defender-side pose means
 *     authoring a seventh, which widens `poseSignature`'s field and owes
 *     `poseSkeleton` a whole posture; and `noteHurt` on the defender, the
 *     nearest thing to hand, says the one thing that did *not* happen. What
 *     the defender gets instead is **the counter the parry bought**, which is
 *     an ATTACK pose the vocabulary already has. That used to run undrawn —
 *     `swingStarted` was an edge polled after `updateWielder` had ticked the
 *     hand, and the counter starts from the enemy step, so the announcement
 *     was gone before anyone looked. Fixed under #142 by latching the edge in
 *     `wielder.ts`; `tests/hitCredit.test.ts` §8 counts the quads.
 *   - **A block** does not rattle anything and flinches nobody. Nobody was
 *     hurt and nothing was staggered; the guard held and the wielder was
 *     *shoved*, which is a directional kick and the only one of the three that
 *     is directional at all. That is the shield's identity in one channel:
 *     you take the weight instead of the wound.
 */
const HIT_SHAKE_FLOOR = 0.04;
const HIT_SHAKE_SPAN = 0.12;
/**
 * Sharper than a light hit and shorter-lived than a heavy one, and *not*
 * derived from the blow's damage: what a parry is worth is the read, not the
 * blow it ate, so a parried skitter bite is as loud as a parried slam.
 */
const PARRY_SHAKE = 0.11;
/** World units of viewpoint shove. `cameraKick` authors 1.2–8.0 for weapons. */
const BLOCK_KICK = 4.5;

/** Issue #172. Bone construct's own card: "Reassembles at 30% HP after a lethal hit." */
const REASSEMBLE_HP_FRACTION = 0.3;
/**
 * Seconds of forced i-frames after a `'reassemble'` revival — issue #172.
 * Long enough that the same hazard tick which killed the body (lava, a
 * lingering hitbox) cannot immediately kill it again before the player has
 * regained control; short enough that it reads as a beat, not a shield.
 */
const REASSEMBLE_IFRAMES = 1.2;

/**
 * Issue #172/#185. `'restance'`'s own HP fraction — higher than reassemble's
 * 0.3. Bone construct's card is a full skeletal reconstruction from
 * fragments; Flesh-machine hybrid's is a mode swap on a chassis that never
 * stopped existing, so less of the body is lost to the beat that saves it.
 */
const RESTANCE_HP_FRACTION = 0.35;
/**
 * Seconds of forced i-frames after a `'restance'` revival — shorter than
 * `REASSEMBLE_IFRAMES`. A stance swap is a flip, not a rebuild, so the
 * player is back in control sooner; it is not zero because the swap forces
 * the *other* stance unconditionally, and that stance is not guaranteed to
 * be the safer one against whatever is still hitting the body.
 */
const RESTANCE_IFRAMES = 0.9;

/**
 * Issue #187. Seconds of forced i-frames after a `'respawn-body'` promotion.
 * No HP fraction alongside it, unlike `reassemble`/`restance`: the survivor
 * that takes over is a body that was already standing in the world with
 * whatever HP the fight left it, not a corpse being rebuilt — the beat is
 * only there so the hit that just felled the old body cannot also fell the
 * new one before control has actually changed hands.
 */
const RESPAWN_BODY_IFRAMES = 1.0;

export interface GameOptions {
  canvas: HTMLCanvasElement;
  uiRoot: HTMLElement;
  seed?: number;
  /**
   * 'world' generates a real procedural world; 'lab' loads the hand-built test
   * chamber. The lab exists because you cannot evaluate movement feel inside
   * procedural terrain — you can't tell whether a bad jump was the controller
   * or the level (docs/02 §3).
   */
  scene?: 'world' | 'lab' | 'prologue';
  mode?: 'blended' | 'layered';
  /** Body archetype id — see meta/archetypes.ts. Default 'duelist'. */
  archetype?: string;
  /**
   * Traversal abilities the run starts with, chosen at setup from what the
   * Attunement track has earned. Merged with the archetype's innate set.
   */
  startAbilities?: string[];
  /** Kit id — see `meta/kits.ts`. Falls back to the profile's stored pick. */
  kit?: string;
  /**
   * The run to resume — issue #51.
   *
   * When present, `Game` adopts it instead of calling `createRunState`, and
   * `seed`, `mode`, `archetype` and `kit` are read off it rather than off the
   * options beside it: the run *is* those choices, and letting the two disagree
   * would rebuild a different world around the same holdings.
   *
   * `meta/runState.ts`'s `isResumable` records, field by field, what a resumed
   * run restores and what it does not — the short version is that the world is
   * regenerated from `run.seed` and the player re-enters at the start with the
   * kit. Callers should pass only a run `isResumable` accepts; a run with an
   * outcome has already been banked.
   */
  run?: RunState;
  /**
   * The profile to read and write — issue #51.
   *
   * `Game` used to load its own, so the shell and the run held two independent
   * `ProfileData` objects and an in-run autosave could revert a setting the
   * player had just changed. The shell worked around that by rewriting the
   * settings after every `stopGame()`. One object, one owner, no workaround.
   */
  profile?: ProfileData;
  /** The save system to persist through. Same single-owner argument (#51). */
  save?: SaveSystem;
}

export class Game {
  readonly rng: Rng;
  readonly cells: CellWorld;
  readonly entities: World;
  readonly input: Input;
  readonly camera: Camera;

  readonly cellSim: CellSim;
  readonly thermal: Thermal;
  readonly charge: Charge;
  readonly pressure: Pressure;
  readonly particles: Particles;
  readonly reactions: ReactionEngine;
  /**
   * Breakable supports — issue #41. The only system in the game that knows a
   * cell can be "holding something up"; it clears STATIC and lets `cellSim` do
   * the rest.
   */
  readonly supports: Supports;
  /**
   * The gathering field — issue #122. Aimed on the movement substep by
   * `stepGather`, stepped once per simulation tick below.
   */
  readonly attract: AttractField;
  private gather: GatherState = createGatherState();
  private gatherCtx!: GatherCtx;
  /**
   * Pixel-mask rigid bodies — issue #42. World objects only; characters stay
   * AABB (`pixelBody.ts:6` stands). Capped at four awake bodies, which is an
   * upload cap and not a physics one — the arithmetic is in `sim/rigid.ts`.
   */
  readonly rigid: RigidBodies;
  /**
   * Where rigid bodies come from, and what the run remembers about them (#84).
   * `spawn` had no callers, so nothing in a real run was a crate. Placement is
   * run-owned and outlives the chunk cache; generation only *seeds*.
   */
  readonly runBodies: RunBodies;
  readonly runTerrain: RunTerrain;
  readonly surfaceTerrain: SurfaceTerrain;
  /**
   * The light field — docs/01 §6.3. A *simulation* system, not a render pass:
   * gameplay has to be able to ask whether the player is in the dark, and a
   * GPU-only field cannot answer that.
   */
  readonly light: LightField;
  private reactionRules: ReturnType<typeof buildReactionRules>;

  private glc: GLContext;
  private terrain: TerrainRenderer;
  private backdrop: BackdropRenderer;
  private lightTex: LightTexture;
  private sprites: SpriteBatch;
  /** Joint positions for the drawn weapon and the drawn tether rope. */
  private readonly weaponStamp = makeWeaponStamp();
  private spineScratch: number[] = [];
  private ropeScratch: number[] = [];
  private bodyRenderer: PixelBodyRenderer;
  readonly bodies: PixelBodySystem;

  player: Entity = -1;
  playerIdx = 0;
  /**
   * Issue #187. Entity indices of the uncontrolled bodies a `bodyCount > 1`
   * archetype spawns alongside `playerIdx` — empty for the ~30 archetypes
   * with `bodyCount: 1`. See `combat/playerFlock.ts` for what they are and
   * are not.
   */
  playerFlock: number[] = [];
  mover: MoverState;
  wielder: WielderState;

  readonly worldGen: WorldGen | null;
  readonly weapons: Map<string, WeaponDef> = new Map();
  readonly modules: Map<string, ModuleDef> = new Map();

  /**
   * The run's holdings — what the loadout panel is an inventory *of*. It used
   * to show the whole game catalogue, so a player holding one pistol was
   * offered forty weapons they could not obtain and buttons that did nothing.
   */
  readonly runWeapons: WeaponInstance[] = [];
  /**
   * Owned module ids. Installed ones stay listed, because removal never
   * consumes a module (docs/04 §7) — it is owned either way.
   */
  readonly runModules: string[] = [];
  /**
   * Player and enemy rounds share one pool. Projectiles resolve through the
   * same `resolveHit` melee uses, so a bullet sees composition, primers and
   * the cell its target is standing in — twelve weapon classes used to deal
   * literally zero damage because nothing ever supplied `onFire`.
   */
  readonly projectiles = new Projectiles();
  /**
   * Summoned entities — the drone class's payload (#16). Separate from the
   * projectile pool on purpose: these have health and can be shot down.
   */
  readonly summons = new Summons();
  private wielderCtx: WielderCtx;
  /** The opening choice. Null once claimed. */
  altar: WeaponAltar | null = null;
  readonly enemies: EnemyRegistry;
  readonly spawner: EnemySpawner;
  private enemyCtx: EnemyAiCtx;
  private ui: UIRoot;
  private uiAdapter: UIAdapter;

  private simAccum = 0;
  private moveAccum = 0;
  private lastTime = 0;
  private rafHandle = 0;
  running = false;
  paused = false;

  /** Terse event feed — the NetHack line from docs/01 §6.4.1. */
  readonly events: string[] = [];
  /** Bumped on every log line so the ticker can cheaply detect change. */
  eventSeq = 0;

  readonly progression: Progression;
  run!: RunState;
  private seed: number;
  readonly archetypeId: string;
  readonly scene: 'world' | 'lab' | 'prologue';
  /** Which prologue beat is live. Empty once it has ended (#22). */
  private prologueBeat = '';
  /** How long Esc has been held, in ms of *game* clock. */
  private prologueSkipMs = 0;
  /** Raised when the prologue ends — `main.ts` swaps to a real run. */
  onPrologueDone: (() => void) | null = null;
  /**
   * Shell exits from the pause menu, set by `main.ts` like `onPrologueDone`.
   *
   * Null until the shell registers them, and the pause menu hides the rows it
   * has no handler for — a menu entry that does nothing is the failure this
   * project keeps finding, so an unregistered exit is absent rather than inert.
   */
  onOpenSettings: (() => void) | null = null;
  onQuitToTitle: (() => void) | null = null;
  /** The run's frame-one loadout — `meta/kits.ts` owns the table. */
  readonly kit: KitDef;
  private startAbilityIds: string[];
  private save: SaveSystem;
  private profile: ProfileData;
  /**
   * Combat's effect on movement, read off the wielder each substep. One
   * retained object: the controller must not allocate at 120 Hz.
   */
  private combatMove: CombatMoveState = {
    attacking: false, drag: 1, jumpCancel: false, staggered: false,
  };
  private moveCtx: MoveContext;
  private abilityCtx: AbilityCtx;

  /** Traversal abilities placed in the world, one per biome — docs/05 §8. */
  readonly caches: AbilityCache[] = [];
  /** The cache the player is standing at, for the prompt. Null when none. */
  cacheInRange: AbilityCache | null = null;
  /** Which passives this profile has earned — resolved once per run (#4). */
  private passives: RunPassives;
  /** True while standing at a rest node: gates stash and crafting. */
  private atRestNode = false;
  /** Cached stash view — rebuilt on a rest-node transition, not per frame. */
  private stashView!: ReturnType<Stash['view']>;
  /** Cached workbench view. Same cadence as `stashView`, plus after a craft. */
  private craftingView!: ReturnType<typeof craftingVM>;
  private craftDirty = true;
  /** Sanctums entered this run, by band — the guaranteed gate (#1 phase 4). */
  private readonly visits = new Map<number, SanctumVisit>();
  /** The pool the player is standing at, for the prompt. */
  sanctumPool: SanctumVisit | null = null;
  /** Vendors placed for the bands entered so far (#11). Rebuilt from seed. */
  private readonly vendors: FactionVendor[] = [];
  /** The vendor the player is standing at, for the prompt and the panel. */
  vendorInRange: FactionVendor | null = null;
  /**
   * The panel's flat view of that vendor. Rebuilt on the frames the UI reads,
   * field-for-field with no derivation — `world/vendor.ts` owns the pricing and
   * the refusals, and a second opinion here would be a second source of truth.
   */
  private vendorView: UIVendorInput | null = null;
  /** Bound once (#25): the frame literal is rebuilt every frame. */
  private readonly roomKindFn = (cx: number, cy: number): string => this.roomKind(cx, cy);
  /** Rest nodes placed for the bands entered so far (#6). */
  private readonly restNodes: RestNode[] = [];
  private readonly restBands = new Set<number>();
  /** The current biome's ambient-hazard sentence, for the map footer. */
  private biomeHazard: string | null = null;
  /** Abilities the archetype and Attunement supply, before anything is found. */
  private baseAbilityMask = 0;
  private visualKey = '';
  private visualCache: WeaponVisual | null = null;
  /**
   * The hand whose weapon the body's single slot is currently drawn holding —
   * #139. Latched on the swing edge and cleared when the pose it belongs to
   * expires; `null` is at rest, and at rest the main hand has the slot.
   *
   * A field rather than a derived expression because the thing being held is a
   * *decision made at an instant* — which hand threw this blow — and the state
   * it would have to be derived from is gone by the second frame of the pose:
   * a projectile node is over in three substeps while the pose it announced
   * runs longer. Every previous attempt at this derived it, and each derivation
   * answered a question adjacent to the one being asked (#92, #127, #137).
   */
  private swingHand: Hand | null = null;

  time = 0;
  showDebug = false;

  /**
   * Debug painter. This is not a toy — it is the only way to test a material
   * interaction deliberately rather than waiting for one to happen. Every
   * reaction in docs/03 §5.2 is reachable by painting two materials together.
   */
  paintMat = 0;
  private paintPalette: number[] = [];
  private paintIndex = 0;

  constructor(opts: GameOptions) {
    registerMaterials();

    // A resumed run is authoritative over the options beside it (#51). Its
    // seed, mode, archetype and kit are what the run *is*; taking them from
    // anywhere else would regenerate a different world around the same
    // holdings, which is precisely the "new run under an old name" this issue
    // exists to prevent.
    const resumed = opts.run ?? null;
    const seed = resumed ? resumed.seed : (opts.seed ?? 0x4e4f4954);
    const scene: 'world' | 'lab' | 'prologue' = opts.scene ?? 'lab';
    this.scene = scene;
    const mode = resumed ? resumed.worldMode : (opts.mode ?? 'layered');
    this.archetypeId = resumed ? resumed.archetype : (opts.archetype ?? 'duelist');
    // A resumed run's abilities live on the run (`traversal.granted`), so the
    // attunement picks are not re-derived — they were spent when it started.
    this.startAbilityIds = resumed ? [] : (opts.startAbilities ?? []);
    this.seed = seed;

    // Progression is the only thing that survives a run. A corrupt save must
    // fall back to a fresh profile rather than bricking the game (docs/11).
    //
    // Injected when the shell has one (#51), so there is a single `ProfileData`
    // and a single `SaveSystem` for the whole application. Loading a second
    // copy here is what let an in-run autosave revert a setting changed at the
    // pause menu.
    this.save = opts.save ?? new SaveSystem();
    this.profile = opts.profile ?? this.save.load().profile ?? createProfile();
    this.progression = new Progression(this.profile.progression);

    this.rng = new Rng(seed);

    if (scene === 'world') {
      // #157: a resumed run's modifiers are what it *was* generated with
      // (same authority rule as seed/mode/archetype above); a fresh run reads
      // the profile's persisted pick. Either way this is the one place a
      // world gets made, so it is the one place that gap could be — and was.
      const modifiers = resolveWorldModifiers(resumed?.modifiers ?? this.progression.effectiveWorldModifiers());
      this.worldGen = new WorldGen({
        seed, mode, cohesion: MODE_COHESION[mode], biomes: buildBiomes(), modifiers,
      });
      this.cells = new CellWorld(this.worldGen.generator);
      // #24: the coarse structural graph is what lets an enemy path between
      // rooms rather than only within line of sight. Registered rather than
      // passed, so no navigation call site changes and no map is old behaviour.
      setNavStructure(this.worldGen.structure);
    } else if (scene === 'prologue') {
      // The prologue is an authored space, not a generated one — docs/08 §2.
      // It has no worldGen, so nothing that reads biomes, bands or sanctums
      // runs; the scene ends by handing off to a real run.
      this.worldGen = null;
      this.cells = new CellWorld(prologueGenerator());
    } else {
      this.worldGen = null;
      this.cells = new CellWorld(testRoomGenerator);
    }

    // Breakable supports — issue #41. Built after `cells` because the system
    // reads the world, and wired back into the generator because registration
    // must happen *inside* chunk generation: chunks are evicted past 256
    // resident and regenerate from the seed, so a group stamped afterwards
    // would come back pointing at cells that had been rebuilt without it.
    // `setGenerator` is safe here — no chunk has been generated yet. The
    // prologue and test-room scenes get a `Supports` with no sites registered:
    // zero groups, zero units per tick.
    this.supports = new Supports(this.cells);
    // The generator is wrapped once, below, after `RunBodies` exists: #84's
    // crate sites decorate *outside* the support sites, so the clearance scan
    // sees a stamped load and cannot stand a crate inside a lava pocket.
    // Deferring is safe — nothing generates a chunk between here and there.

    this.entities = new World(2048);
    this.input = new Input(opts.canvas);
    this.camera = new Camera();

    this.particles = new Particles(this.cells, this.rng);
    // Anything displaced by placement that cannot find a neighbouring cell
    // becomes a particle rather than being deleted (docs/11 §5.5).
    this.cells.onEvict = (x, y, mat, temp) => {
      this.particles.emit(x, y, mat, 1, 45, 0.8, 0, -1, temp);
    };
    this.cellSim = new CellSim(this.cells, this.rng);
    this.thermal = new Thermal(this.cells, this.rng);
    this.charge = new Charge(this.cells, this.rng);
    this.attract = new AttractField(this.cells);
    this.pressure = new Pressure(this.cells, this.rng);
    this.reactions = new ReactionEngine(this.cells, this.rng);
    // After `particles`, because matter a body lands on is *moved* into the
    // free-particle layer rather than deleted (`promote` clears the source
    // cell), and after `cells`, because it hooks `onCellDamaged` — one of the
    // two readers of the sleeping-body ownership map, and the reason a settled
    // crate wakes when you dig the floor out from under it.
    this.rigid = new RigidBodies(this.cells);
    this.rigid.displaceTo = this.particles;
    this.cells.onCellDamaged = (x, y) => this.rigid.noteCellDisturbed(x, y);
    // The entity end of the rigid solver, and it serves **two** callers: a body
    // that crushes, and a door that senses. One query rather than two
    // interfaces is deliberate — nothing can hold a door open that the door
    // cannot also see.
    //
    // #42 shipped `CrushSink` with no assignment anywhere but its tests, so
    // until this line the docs/01 §5.1 asymmetry was a rule about an event that
    // could not happen in a real run: a crate had never crushed anything a
    // player could see.
    this.rigid.crush = {
      query: (x0, y0, x1, y1, out) => {
        const w = this.entities;
        w.each(Comp.HEALTH, (i) => {
          if (w.hp[i] <= 0) return;
          if (w.x[i] + w.halfW[i] < x0 || w.x[i] - w.halfW[i] > x1) return;
          if (w.y[i] + w.halfH[i] < y0 || w.y[i] - w.halfH[i] > y1) return;
          out.push(i);
        });
      },
      isPlayer: (i) => (this.entities.mask[i] & Comp.PLAYER) !== 0,
      // `srcMat` is non-zero so i-frames do not cover it: a combat grant must
      // not make the environment free, and a falling crate is the environment.
      damage: (i, amount) =>
        this.damageEntity(i, amount, DamageType.PHYSICAL, -1, MAT.WOOD),
    };
    // #84. Where crates are is pure in `(seed, cx, cy)` and re-derived on every
    // chunk generation; what the player did to one is a run-owned diff. Built
    // after `rigid` because it drives it, and it takes the generator over here.
    this.runBodies = new RunBodies(this.rigid, this.cells);
    // After `onCellDamaged` is assigned above, because `RunTerrain` chains that
    // hook rather than replacing it -- `rigid.noteCellDisturbed` must survive.
    this.runTerrain = new RunTerrain(this.cells);
    // The profile's marks on the surface — docs/27 §12 item 1. Same chaining
    // argument; the two recorders partition the world at y = 0 and neither
    // records the other's half (surfaceTerrain.ts's header).
    this.surfaceTerrain = new SurfaceTerrain(this.cells, () => this.run?.tick ?? 0);
    if (this.worldGen) {
      this.cells.setGenerator(
        // Outermost: the player's marks are the last thing that happened to the
        // world, so a crate site stamped over a blown-out floor would otherwise
        // stand a crate in mid-air (#117). The surface log sits directly
        // inside the run log; the two are disjoint by the partition, so their
        // order cannot matter.
        withRunDiffs(
        withSurfaceDiffs(
        withCrateSites(
          // #42's doors level a sill and carve their own hole and recess, so
          // they decorate *inside* the crate sites: the crate's clearance scan
          // then runs against the carved chunk and cannot stand a crate on a
          // trapdoor — a fine piece of content and an appalling first one,
          // because the crate falls through the instant the player steps on
          // the plate and both bodies are then awake, moving, and half the
          // upload budget. Outside `withSupportSites` for the same reason
          // crates are: the clearance scan has to see a stamped load.
          withDoorSites(
            withSupportSites(this.worldGen.generator, seed, this.supports),
            seed,
            this.runBodies,
          ),
          seed,
          this.runBodies,
        ),
        this.surfaceTerrain,
        ),
        this.runTerrain,
        ),
      );
    }
    this.light = new LightField(this.cells);
    // Retained: the codex enumerates these to build its reaction list, and
    // rebuilding them there would produce a second, divergent table.
    this.reactionRules = buildReactionRules();
    this.reactions.compile(this.reactionRules);

    this.glc = new GLContext(opts.canvas);
    this.terrain = new TerrainRenderer(this.glc);
    this.backdrop = new BackdropRenderer(this.glc);
    this.lightTex = new LightTexture(this.glc.gl);
    this.sprites = new SpriteBatch(this.glc);
    this.bodyRenderer = new PixelBodyRenderer(this.glc);
    this.bodies = new PixelBodySystem();

    this.moveCtx = {
      cells: this.cells,
      particles: this.particles,
      combat: this.combatMove,
      onLand: (impact) => {
        // Trauma scales with impact past the soft threshold, so ordinary
        // landings don't shake the screen at all.
        if (impact > 300) this.camera.shake(Math.min(0.45, (impact - 300) / 900));
      },
      onDash: () => this.camera.shake(0.05),
      onWallJump: () => this.camera.shake(0.04),
    };

    // Abilities get their own context for the same reason movement does: they
    // write cells and promote particles, so they need the world, not just the
    // entity. This was declared `!` and never assigned — every traversal
    // ability was one keypress away from a TypeError inside the frame loop,
    // which `main.ts`'s error boundary would have turned into a dead page.
    this.abilityCtx = {
      cells: this.cells,
      particles: this.particles,
      // docs/02 §3 row 9: the updraft column launches enemies too. The ECS
      // iterates; the ability layer only reads the rows it is handed.
      forEachBody: (fn) => this.entities.each(Comp.BODY, fn),
      onEvent: (msg) => this.log(msg),
      onShake: (amount) => this.camera.shake(amount),
    };

    // ---- combat content
    for (const wdef of buildWeapons()) this.weapons.set(wdef.id, wdef);
    for (const mdef of buildModules()) this.modules.set(mdef.id, mdef);

    // `statScaling` measures deviation from `defaultStats()`, so a level-1
    // wielder is exactly today's damage and every weapon's `scaling` block goes
    // live the moment progression grants a point.
    this.wielder = createWielder(statsForLevel(1));
    this.wielderCtx = {
      weapons: this.weapons,
      modules: this.modules,
      rng: this.rng,
      // The hinge between combat and traversal (docs/02 §2.6): confirming a hit
      // refreshes dash and Airstep, so aggression *is* mobility.
      onFire: makeFireHandler(this.projectiles, this.entities, this.rng),
      // A drone is an entity, not a round, so it does not go through onFire.
      onSummon: makeSummonHandler(this.summons, this.entities),
      // The relic blade is the only class that asks where it is (docs/04 §3.1).
      // A callback, not a value: the answer changes as the player walks.
      biomeId: () =>
        this.worldGen
          ? this.worldGen.biomeAt(this.entities.x[this.playerIdx], this.entities.y[this.playerIdx]).id
          : '',
      // The scythe's corpse harvest — the one thing that puts crafting inputs
      // into the pouch (`src/combat/harvest.ts`). Without this line the verb is
      // wired and inert, and all 10 crafting inputs stay unreachable.
      //
      // A callback rather than a value, for the same reason `biomeId` is:
      // `this.run` is replaced on every death, and a captured stale pouch would
      // silently swallow everything the player harvested after their first run.
      pouch: () => this.run ?? null,
      // There is exactly one `WielderState` in the game and it is the player's
      // (`createWielder` above is the only call; enemies attack through
      // `enemyAttacks.ts`, which reaches this context only for `parry`). So a
      // confirm arriving *here* was thrown by the player, and `this.playerIdx`
      // is the credit, not a guess. Stated rather than assumed because
      // `creditHitConfirm` is shared with the projectile pool, which is not the
      // player's — see #138 and the hook in `stepMovement`.
      //
      // `hand` is dropped deliberately: nothing below is hand-shaped. See
      // `creditHitConfirm`.
      onHitConfirm: (_s, _hand, target, result) =>
        this.creditHitConfirm(this.playerIdx, target, result),
      onCameraKick: (amount, dx, dy) => this.camera.kick(dx * amount, dy * amount),
    };

    this.mover = this.spawnPlayer();
    // No starting weapon is granted: the altar is where the run's first choice
    // is made. Only the off-hand is issued, so the player is never defenceless
    // while deciding.
    this.passives = resolvePassives(this.progression);
    // The lab is a bench: every weapon in the stash, and the stash always open.
    //
    // Comparing two weapons means swinging both against the same ground, and
    // outside the lab that costs two runs plus whatever the stash happens to
    // hold. Here it costs a Tab. The stash is normally gated on standing at a
    // rest node (#6) and sized by `stashSlots()`; the lab has no rest nodes at
    // all, so without both overrides the panel opens onto "not at a rest node"
    // and the shelf is empty anyway.
    //
    // It cannot leak. The lab is constructed with a null-storage `SaveSystem`
    // — `main.ts` says why — so nothing here is written, and `reload()`
    // re-reads the profile from disk, which is the same reasoning that already
    // licenses the lab to touch the in-memory profile.
    if (this.scene === 'lab') this.stockLabBench();
    this.stashView = new Stash(this.progression).view(this.scene === 'lab');
    this.kit = getKit(resumed ? resumed.kit : (opts.kit ?? this.progression.effectiveStartKit()));
    // The kit is the run's frame-one loadout (docs/05 §4). The altar still
    // runs — its role changes from "your only weapon" to an upgrade decision.
    this.equipKit();

    // ---- ecology
    this.enemies = new EnemyRegistry();
    // The underground's own dressing — issue #57's light/weather/density
    // channel, pure in the run seed rather than the profile's surfaceSeed
    // (docs/27 §3.1: everything below y=0 resets every run). Fixed for the
    // run's lifetime, so this is the only call site — unlike the surface
    // dressing there is no re-apply-on-heal path to also cover.
    const mood = undergroundDressing(seed);
    this.spawner = new EnemySpawner(this.enemies, seed, {
      perChunk: mood.perChunk,
      contamination: mood.contamination,
    });
    setUndergroundAmbientMul(mood.ambient);
    this.spawner.bodies = this.bodies;
    this.enemyCtx = {
      ...makeEnemyAttacks({
        world: this.entities, cells: this.cells, projectiles: this.projectiles, rng: this.rng,
        damageEntity: (i, a, t, sIdx) => this.damageEntity(i, a, t, sIdx),
        playerIdx: () => this.playerIdx,
        wielder: this.wielder, wielderCtx: this.wielderCtx,
        // Two effects, two owners — the same split `creditHitConfirm` makes, and
        // for the same reason (#138's sibling).
        //
        // `boxHit` selects every HEALTH entity that is not on the striker's team
        // and not `NEUTRAL`, so `target` is anything on the player's side —
        // *including the player's own drones*, since `Summons.deploy` copies
        // `w.team[owner]`. The hurt pose is the target's and stays ungated. The
        // camera is the viewpoint's, so it asks `playerInExchange`; otherwise an
        // enemy chewing on a drone across the room rattles the player's screen.
        //
        // `srcIdx` is an enemy at every reachable call site today, so the shared
        // predicate's first disjunct is dead here. It is still routed through
        // the predicate rather than hand-written as `target === playerIdx`,
        // because a third copy of "is the player in this exchange" is how this
        // codebase produced four *selection and lookup disagree* defects.
        //
        // `amount` is read (#141). It arrives post-effectiveness, so it is the
        // damage the target actually lost, and the shake is a floor plus that
        // — the same ramp `presentHit` scales the wound spray by. Flat was the
        // alternative and it is the wrong one: the camera is the only channel
        // that reports *force*, the hurt pose is a fixed 0.18 s and the wound
        // already varies, so a flat shake left the two halves of one blow
        // disagreeing about how hard it was.
        onHit: (target, srcIdx, amount) => {
          this.bodies.noteHurt(target);
          if (this.playerInExchange(srcIdx, target)) {
            this.camera.shake(HIT_SHAKE_FLOOR + HIT_SHAKE_SPAN * blowSeverity(amount));
          }
        },
        // ------------------------------------------------------- the two that
        // were declared, called, and supplied by nobody (#141). Parrying or
        // blocking produced *no presentation at all*: the two defensive verbs
        // docs/04 treats as first-class were the two you could not tell from a
        // whiff except by the damage you did not take.
        //
        // Both are reachable only when `target === playerIdx()` —
        // `enemyAttacks.strike` asks `resolveIncomingBlow` for nobody else, and
        // there is exactly one `WielderState` in the game. The camera still
        // goes through `playerInExchange` rather than being written as an
        // unconditional shake, for the reason that method's docblock gives: the
        // rule is one rule, and a call site that re-spells it as its own
        // collapsed form is how four *selection and lookup disagree* defects
        // started.
        //
        // The flinch is the **attacker's**, and that is the whole difference
        // from `onHit`. A parry cancels the blow and stamps its thrower with
        // the parry's primer, so the recoil belongs to the thing that was
        // parried; posing the *defender* as hurt would say the opposite of what
        // just happened.
        onParried: (target, srcIdx) => {
          this.bodies.noteHurt(srcIdx);
          if (this.playerInExchange(srcIdx, target)) this.camera.shake(PARRY_SHAKE);
        },
        // No shake and no pose. A block is absorbed rather than survived: the
        // guard bar pays, and the viewpoint is shoved along the blow. See
        // `enemyAttacks.EnemyAttackDeps.onBlocked` for why the direction is
        // carried here and not re-derived from the two positions.
        onBlocked: (target, srcIdx, dirX, dirY) => {
          if (this.playerInExchange(srcIdx, target)) {
            this.camera.kick(dirX * BLOCK_KICK, dirY * BLOCK_KICK);
          }
        },
      }),
      registry: this.enemies,
      // The windup *is* the tell. `behaviors.ts` announces the envelope an
      // enemy has just entered, and this is the only thing that turns that
      // announcement into a pose — until now the sole `noteAttack` caller was
      // the player's own swing, so all 54 enemies committed to their attacks
      // silently and docs/05 §7's "every death is traceable" was not met.
      // `noteAttack` clamps and ignores entities with no attached body, so an
      // unposed entity is safe.
      noteAttack: (idx, dur) => this.bodies.noteAttack(idx, dur),
      damageEntity: (idx, amount, type, srcIdx) => this.damageEntity(idx, amount, type, srcIdx),
      spawnEnemy: (id, x, y) => {
        const def = this.enemies.get(id);
        if (!def) return -1;
        const e = this.spawner.spawn(this.entities, this.cells, def, x, y);
        return e === -1 ? -1 : e & 0xfffff;
      },
      onDeath: (idx, def) => {
        this.spawner.notifyDeath();
        this.bodies.detach(idx);
        const tier = def.rank === 2 ? 'boss' : def.rank === 1 ? 'elite' : 'base';
        // Both currencies are paid by `recordKill` itself — the rates live in
        // meta/runState.ts, and a kill cannot be recorded without paying for
        // it. That is deliberate: `addAsh` sat unused for the whole project
        // precisely because the payout was a separate call site.
        const paid = recordKill(this.run, def.id, tier, def.factions);
        this.log(`${def.name.toLowerCase()} destroyed · +${paid.scrip} scrip`);
        // #11/#70: opposed-axis movement must be *observable* or the zero-sum
        // design is invisible. It was going out on `this.log` — the channel
        // reserved for material reactions and altar rerolls — for the one event
        // that reorganises the world around you: a shop closing, a patrol
        // turning hostile. `announce` puts it in the log *and* the toast in one
        // call, so the two cannot disagree.
        //
        // Still only band crossings. A line per reputation point would bury the
        // ticker, and `banded` exists on `RepChange` precisely so "the moment
        // the world changes" is a value rather than a heuristic.
        //
        // The loop can announce twice, and should. A base kill moves one point
        // and the band table is not symmetric about zero, so the two poles of an
        // axis never cross on the same body — but an elite is worth three and a
        // boss eight, and a single boss can carry a pair over both thresholds at
        // once. Two bands changed; two lines is the honest report.
        for (const c of paid.reputation) {
          if (!c.banded) continue;
          this.announce(`${c.name} — ${c.standing}`, {
            log: `${c.name.toLowerCase()} — now ${c.standing}`,
            // Both halves, always. A toast naming only the faction you angered
            // hides the half that makes the kill a decision, which is #11's
            // acceptance criterion and the reason `factionSwingText` takes two
            // names rather than one.
            swing: { gained: c.name, lost: getFaction(opposedOf(c.faction)).name, delta: c.delta },
          });
        }
        // Kills are the in-run source of modules (#9). The pool is gated by
        // exactly the call `raiseAltar` puts on the altar's weapons — the point
        // where the Salvage track's five module unlocks stop being bookkeeping.
        // Crafting (#8) is the reliable path and reaches kinetic only; drops
        // are the lucky one and are weighted away from it.
        const drop = rollModuleDrop(
          unlockedModuleIds(this.progression, [...this.modules.values()]),
          this.modules,
          this.runModules,
          dropSourceFor(def),
          this.rng,
        );
        if (drop !== null) {
          this.acquireModule(drop);
          const md = this.modules.get(drop)!;
          this.announce(md.name, { log: `salvaged ${md.name.toLowerCase()}` });
        }
      },
      // Shapers are the identity family (docs/05 §3) and the reason a shared
      // cell-write budget exists: three of them in one room must stay
      // affordable, so they compete for a pool rather than each having one.
      maxEnemiesPerTick: 24,
      maxCellWritesPerTick: 900,
      maxProbesPerEntity: 48,
      destroyOnDeath: true,
    };

    this.uiAdapter = new UIAdapter(this.weapons, this.modules, {
      enemies: this.enemies,
      progression: this.progression,
      reactionRules: this.reactionRules,
      // Authoring affordance only. The panel is an inventory; a browsable
      // catalogue of unobtainable gear teaches that its buttons do not work.
      devCatalogue:
        typeof location !== 'undefined' &&
        new URLSearchParams(location.search).get('catalogue') === 'all',
    });
    // Codex thumbnails ride in `settings`, which is already a loose string map,
    // so restoring them needs no save-schema change (#26).
    this.uiAdapter.importThumbs(String(this.profile.settings.codexThumbs ?? ''));

    this.ui = new UIRoot(opts.uiRoot, {
      // The shell's two pause exits. Read through the fields rather than
      // captured, because `main.ts` registers them after construction — and
      // `hasShellExits` is a predicate for the same reason: the pause menu asks
      // at the moment it builds its rows, so an exit that was registered late
      // still gets a row and one that was never registered still gets none.
      onOpenSettings: () => this.onOpenSettings?.(),
      onQuitToTitle: () => this.onQuitToTitle?.(),
      hasShellExits: () => ({
        settings: this.onOpenSettings !== null,
        quitToTitle: this.onQuitToTitle !== null,
      }),
      onEquip: (weaponId, hand) => this.equipById(weaponId, hand),
      onUnequip: (hand) => equip(this.wielder, hand, null, this.wielderCtx),
      // Nothing is destroyed: stashing empties the hand and leaves the weapon
      // in the run's holdings.
      onStash: (weaponId) => {
        const hand = this.handHolding(weaponId);
        if (hand) equip(this.wielder, hand, null, this.wielderCtx);
      },
      // Keyed by `StashItemVM.id`, not by a weapon id: the stash holds
      // definitions with their own stable ids, and two stashed copies of one
      // weapon are two rows.
      onUnstash: (stashItemId) => {
        const res = new Stash(this.progression).withdraw(stashItemId, this.atRestNode);
        if (!res.ok || !res.item) {
          this.log(res.reason ?? 'nothing to take');
          return;
        }
        const it = res.item;
        if (it.kind === 'module') {
          this.acquireModule(it.defId);
        } else {
          // The same weapon with the same build, not the same roll — the stash
          // stores definitions (meta/stash.ts).
          const inst = rollWeapon(it.defId, this.rng);
          for (const m of it.modules) if (this.modules.has(m)) inst.modules.push(m);
          this.acquireWeapon(inst);
        }
        this.stashView = new Stash(this.progression).view(this.atRestNode);
        this.persistProfile();
      },
      onCraft: (recipeId, args) => {
        const res = craft(this.run, this.progression, recipeId, args, this.atRestNode);
        this.log(res.message);
        this.craftDirty = true;
        if (!res.ok || !res.outcome) return;
        // `craft` returns an instruction rather than mutating combat state —
        // installing a module and rerolling a weapon are combat/'s business.
        if (res.outcome.kind === 'module' && !this.runModules.includes(res.outcome.moduleId)) {
          this.runModules.push(res.outcome.moduleId);
        }
        this.persistProfile();
      },
      onInstallModule: (weaponId, slot, slotIndex, moduleId) =>
        this.installModule(weaponId, slot, slotIndex, moduleId),
      onRemoveModule: (weaponId, slot, slotIndex) =>
        this.removeModule(weaponId, slot, slotIndex),
      onToggleFavourite: (kind, id) => this.uiAdapter.toggleFavourite(kind, id),
      onSaveLoadout: (index, name) => this.uiAdapter.saveLoadout(index, name),
      onRestoreLoadout: (index) => {
        const l = this.uiAdapter.restoreLoadout(index);
        if (!l) return;
        this.equipById(l.mainId, 'main');
        this.equipById(l.offId, 'off');
        // docs/04 §7 asks a loadout to snapshot "weapons + modules" (#17).
        this.restoreModules(l.mainId, l.mainModules);
        this.restoreModules(l.offId, l.offModules);
        // A partial restore is better than a refusal, but it must never be
        // silent: a build that came back different under the same name is the
        // whole failure this issue names.
        if (!l.exact) this.log(`loadout restored without ${l.missing.join(', ')}`);
      },
      onClearLoadout: (index) => this.uiAdapter.clearLoadout(index),
      onSelectPair: (index) => {
        this.uiAdapter.selectPair(index);
        const p = this.uiAdapter.pair(index);
        if (!p) return;
        this.equipById(p.mainId, 'main');
        this.equipById(p.offId, 'off');
      },
      onSavePair: (index) => this.uiAdapter.savePair(index),
      onResume: () => {
        this.paused = false;
        this.input.setEnabled(true);
      },
      // Deliberately not `onQuit`. By the time this fires the run has already
      // been ended and banked — that is what the numbers on the screen are —
      // so routing Continue through `onQuit` would bank it a second time: Ash
      // paid twice, `stats.deaths` incremented twice, the Courier's carry
      // collected twice.
      onContinueAfterRun: () => this.startNextRun(),
      onQuit: () => {
        // Abandon: the same two halves back to back, with no screen between.
        // (Abandon is currently banked as a *death* — docs/23 §7.5 — which is
        // a separate defect and is not fixed here.)
        // `'abandon'`, not `'death'` (#108). `endRun` counts only a death and
        // `bankRun` gates the Ash payout, the Courier's carry and the Echo on
        // the same word — so while this said `'death'`, walking away was a
        // strictly better way to farm the Echo than dying for it.
        this.finishRun('abandon', null, null);
        this.ui.setScreen('none');
        this.startNextRun();
      },
      onScreenChange: (s) => {
        // Panels report intent; pausing is the integrator's business.
        // Every panel pauses except the death screen, which is the whole
        // design of docs/23: the corpse is still in the lava and the fire is
        // still spreading while the player reads why they died. Input stays
        // deaf either way — a corpse takes no orders.
        this.paused = s !== 'none' && s !== 'gameover';
        this.input.setEnabled(s === 'none');
      },
    });

    // Generate the starting neighbourhood before the first movement substep.
    // Movement runs before the first `updateActive` in the frame order, and
    // `isSolid` reads ungenerated space as empty air — without this the player
    // falls through the floor on frame one.
    this.cells.updateActive(
      this.camera.renderX, this.camera.renderY, this.camera.viewW, this.camera.viewH,
    );

    this.groundPlayer();

    // **The read that did not exist** — issue #51. Every autosave serialised
    // this object and nothing anywhere loaded it back, so eleven fields of real
    // play were written to disk and discarded on the next boot. `opts.run` is
    // that reader's other end.
    //
    // Not calling `createRunState` is load-bearing beyond the obvious: that
    // function *consumes* the Ash Courier's carry and stamps the Cartographer's
    // Echo. Resuming skips it, so neither is collected twice — a property of
    // not creating a run rather than a rule someone has to remember.
    //
    // `archetype: this.archetypeId` — found alongside #161, not by it: this is
    // the first-run twin of the gap `startNextRun`'s own `createRunState` call
    // already carries a comment about ("passed because they were not, and the
    // omission was invisible until something read the run back"). Without it
    // `archetypeId` defaults to `DEFAULT_ARCHETYPE`, so `traversal.granted`'s
    // innate half — and therefore `hasAbility` for any innate — was computed
    // against whatever body the default happens to be, not the one the player
    // chose, on every very first run of a profile.
    this.run = resumed ?? createRunState({
      seed, worldMode: mode, archetype: this.archetypeId, progression: this.progression, kit: this.kit.id,
    });

    // `RunState.bodies` becomes the ledger's own array, so an autosave taken at
    // any moment carries the current one and there is nothing to remember to
    // flush. A resumed run's ledger is read back here (#84).
    // Here and not in the constructor: `pouch` and `ledger` are `this.run`,
    // which does not exist until the line above (#122).
    this.gatherCtx = {
      field: this.attract,
      pouch: this.run,
      ledger: this.run,
      onEvent: (msg) => this.log(msg),
      forEachBody: (fn) => this.entities.each(Comp.BODY, fn),
    };
    this.gather = createGatherState();
    // The pool is the one thing on the surface that rewards rather than
    // refuses, and a pool with nothing behind it teaches "do not bother
    // exploring" a run earlier than no pool at all (docs/27 §2). `cling` is the
    // natural default and not a coincidence: it is the ability that opens the
    // branch the player has just been refused by. `TREE_HOLLOW_SITE` is
    // guaranteed dry, standable, clear to the body's full height and walkable
    // to from spawn with no ability -- `tests/greatTree.test.ts`.
    if (this.worldGen) {
      const hollow: AbilityCache = {
        x: TREE_HOLLOW_SITE.x,
        y: TREE_HOLLOW_SITE.y,
        abilityId: nextAbility(runAbilities(this.run)) ?? 'cling',
        claimed: false,
      };
      buildCache(this.cells, hollow);
      this.caches.push(hollow);
      // The east half's pool — docs/27 §12 item 6. On the mesa past the
      // crest, visible from the refusal, reachable only by the ability the
      // climb withholds — and it holds the ability *after* the hollow's, so
      // the surface's two rewards are a sequence rather than a choice.
      const mesa: AbilityCache = {
        x: desertCacheSite().x,
        y: desertCacheSite().y,
        abilityId:
          nextAbility([...runAbilities(this.run), hollow.abilityId]) ?? 'phase-drift',
        claimed: false,
      };
      buildCache(this.cells, mesa);
      this.caches.push(mesa);
    }
    // The kit was issued before `this.run` existed; bank it now.
    this.adoptHoldings();
    this.runBodies.bind(this.run);
    // #118. Only a `world` scene adopts the run's terrain log. The prologue and
    // the test room are authored spaces whose generators ignore `run.seed`, and
    // the prologue shares the real save -- so a dig there is recorded onto a run
    // Continue offers as a *world* run, and replayed as a hole in a procedural
    // cave the player never dug. The seed guard cannot see it: same run, same
    // seed.
    if (this.worldGen) this.runTerrain.bind(this.run);
    // The surface log binds beside the run log and under the same #118 guard,
    // with the difference surfaceTerrain.ts's bind names: the run is new and
    // the profile is not, so this replays the same marks into the new world.
    if (this.worldGen) this.surfaceTerrain.bind(this.profile);
    // The same clock dresses the surface: which enemies are out, and what the
    // daylight is doing. Applied wherever the profile binds, so an advanced
    // surfaceSeed re-dresses the world the same moment it heals it.
    if (this.worldGen) {
      const day = surfaceDressing(this.profile.surfaceSeed);
      this.spawner.setSurfaceRoster(day.roster);
      setSurfaceAmbientMul(day.ambient);
    }

    // Traversal found *in the world* this run. `spawnPlayer` built the mask
    // from the archetype's innates and the attunement picks, which is the right
    // answer for a new run and half an answer for a resumed one: without this,
    // continuing would re-lock every door the player had already opened, and
    // the ability caches would be re-earnable. `baseAbilityMask` is deliberately
    // left alone — a death restarts from the archetype package, resumed or not.
    if (resumed) {
      for (const id of resumed.traversal.found) {
        const bit = ABILITY_BY_ID[id];
        if (bit) this.mover.abilities |= bit;
      }
    }

    if (scene === 'prologue') this.prologueBeat = PROLOGUE_BEAT_IDS[0];
    this.wireEvents();
    // Grants with no system yet name themselves once, on the console and in the
    // ticker, rather than being silently absent (issue #2).
    this.log(describeGrants(resolveGrants(this.progression)));
    this.paintPalette = [
      MAT.WATER, MAT.OIL, MAT.SAND, MAT.ACID, MAT.LAVA,
      MAT.COAL_DUST, MAT.SNOW, MAT.BLOOD, MAT.MERCURY,
    ];
    this.paintMat = this.paintPalette[0];
    this.bindPaintKeys();
  }

  private bindPaintKeys(): void {
    window.addEventListener('keydown', (e) => {
      const n = Number(e.key);
      if (Number.isInteger(n) && n >= 1 && n <= this.paintPalette.length) {
        this.paintIndex = n - 1;
        this.paintMat = this.paintPalette[this.paintIndex];
        this.log(`painting ${materialDef(this.paintMat).label.toLowerCase()}`);
      }
    });
  }

  /**
   * Left-click paints the selected material; right-click injects heat.
   *
   * Gated behind the debug overlay (F3). It shares its buttons with the attack
   * actions, so leaving it always-on meant every swing also sprayed matter at
   * the cursor — which made it look like the mouse *only* painted and that
   * weapons did nothing.
   */
  private handlePainting(): void {
    if (!this.showDebug) return;
    const p = this.input.pointer;
    if (!p.inside) return;
    const paint = this.input.held('mainAttack');
    const ignite = this.input.held('offAttack');
    if (!paint && !ignite) return;

    const world = { x: 0, y: 0 };
    this.camera.screenToWorld(p.x, p.y, this.glc.width, this.glc.height, world);
    const wx = Math.floor(world.x);
    const wy = Math.floor(world.y);

    if (paint) {
      // Emit as particles rather than stamping cells, so painted matter
      // arrives with velocity and behaves like it was thrown, not spawned.
      this.particles.emit(wx, wy, this.paintMat, 6, 40, 0.9, 0, 1);
      // Solids are *placed*, which displaces any liquid already there — the
      // general answer to a flooded route (docs/11 §2.2).
      if (M.solid[this.paintMat] === 1) {
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) this.cells.place(wx + dx, wy + dy, this.paintMat);
        }
      }
    }
    if (ignite) {
      this.cells.heatCircle(wx, wy, 5, 900);
      this.charge.inject(wx, wy, 1);
    }
  }

  /**
   * Hand over the run's kit — docs/05 §4.
   *
   * The off-hand used to be a hardcoded pistol and the main hand stayed empty
   * until the altar, so all eight `kind: 'kit'` unlocks bought nothing.
   */
  /**
   * Put one of every weapon on the lab's shelf.
   *
   * Derived from the catalogue rather than listed, so a weapon added tomorrow
   * is on the bench without anybody remembering to add it here — which is the
   * failure this project has found fifteen times in the other direction, as an
   * authored field nothing reads.
   *
   * `stashSlots` is raised to fit rather than the deposit being allowed to
   * overflow: the capacity is what the panel prints and what `freeSlots`
   * computes against, so a shelf longer than the stash claims to be would
   * misreport both.
   */
  private stockLabBench(): void {
    const all = [...this.weapons.values()];
    this.progression.state.stash = all.map((wp) => ({
      id: `lab:${wp.id}`,
      defId: wp.id,
      kind: 'weapon' as const,
      label: wp.name,
      modules: [],
    }));
    this.progression.state.labStashSlots = all.length;
  }

  private equipKit(): void {
    const pick = (id: string | null, fallbackClass: string): string | null => {
      if (!id) return null;
      if (this.weapons.has(id)) return id;
      // Degrade to the class rather than to nothing: a kit naming a weapon this
      // build does not ship costs the player that weapon, not the run.
      for (const [wid, def] of this.weapons) {
        if (String(def.class) === fallbackClass) return wid;
      }
      return this.weapons.keys().next().value as string;
    };

    const issue = (id: string | null, hand: 'main' | 'off', mods: readonly string[]): void => {
      const resolved = pick(id, hand === 'off' ? 'pistol' : 'sword');
      if (!resolved) {
        equip(this.wielder, hand, null, this.wielderCtx);
        for (const m of mods) {
          if (this.modules.has(m)) this.acquireModule(m);
        }
        return;
      }
      const inst = rollWeapon(resolved, this.rng);
      for (const m of mods) if (this.modules.has(m)) inst.modules.push(m);
      this.acquireWeapon(inst);
      equip(this.wielder, hand, inst, this.wielderCtx);
    };

    issue(this.kit.main, 'main', this.kit.modules);
    issue(this.kit.off, 'off', []);
    this.stockLabWheel();
  }

  /**
   * Fill the lab's hold-Tab wheel.
   *
   * `stockLabBench` stocks the *stash*, which is a different list: the radial
   * draws eight fixed wedges (docs/04 §7) fed by `inventory.slots`, and the
   * adapter fills those from the two hands followed by what the run carries.
   * So the wheel is filled by making the lab *carry* six more weapons, chosen
   * by `labWheelDefs` so the eight wedges are eight different motions.
   *
   * Called from `equipKit` rather than beside `stockLabBench` because the
   * hands are two of the eight and are not known until the kit is issued —
   * and because `equipKit` is also what a restart re-runs, so the wheel
   * refills for the next lab session without a second call site.
   *
   * `labWheelDefs` returns nothing for any scene but the lab: the guard is a
   * property of the function, not of this line.
   */
  private stockLabWheel(): void {
    const hands = [this.wielder.main.instance, this.wielder.off.instance].map((i) =>
      i ? (this.weapons.get(i.defId) ?? null) : null,
    );
    for (const def of labWheelDefs(this.scene, [...this.weapons.values()], hands)) {
      this.runWeapons.push(rollWeapon(def.id, this.rng));
    }
  }


  /**
   * Raise the altar on a sanctum's plinth (#1 phase 4).
   *
   * It used to stand at spawn, which made it a second opening choice layered
   * on the one the setup screen already asks for. As the sanctum's, it becomes
   * the pacing beat the descent did not have: you cross a band, and the game
   * gives you somewhere to spend what you found on the way down. Worldgen has
   * already carved the plinth, so nothing is built here.
   */
  private raiseAltar(ax: number, ay: number): void {
    // Only unlocked weapon classes reach the altar. This is the point where
    // the Salvage track stops being bookkeeping and starts changing a run.
    const allowed = new Set(unlockedWeaponIds(this.progression, [...this.weapons.values()]));
    const pool = new Map<string, WeaponDef>();
    for (const [id, def] of this.weapons) if (allowed.has(id)) pool.set(id, def);

    this.altar = new WeaponAltar(
      pool.size > 0 ? pool : this.weapons, this.modules, this.rng,
      ax, ay,
    );
    this.altar.onReroll = () => this.log('the altar turns over');
    this.altar.onClaim = (offer) => {
      this.acquireWeapon(offer.instance);
      for (const id of offer.instance.modules) {
        if (!this.runModules.includes(id)) this.runModules.push(id);
      }
      equip(this.wielder, 'main', offer.instance, this.wielderCtx);
      this.announce(`${offer.resolved.def.name} claimed`, {
        log: `took ${offer.resolved.def.name.toLowerCase()}`,
      });
      this.altar = null;
    };
    this.log('choose a weapon');
  }

  // ------------------------------------------------------------- loadout ops
  //
  // Keyed on `WeaponInstance.id`, never on `defId`. Two rolls of the same
  // definition are two different weapons — that is what `rolled` is for — and
  // a defId lookup returned whichever came first, so equipping the second
  // sword handed you the first one's stats and a module socketed onto a weapon
  // the player was not looking at.

  private instanceFor(id: string): WeaponInstance | null {
    if (this.wielder.main.instance?.id === id) return this.wielder.main.instance;
    if (this.wielder.off.instance?.id === id) return this.wielder.off.instance;
    return this.runWeapons.find((i) => i.id === id) ?? null;
  }

  private handHolding(id: string): 'main' | 'off' | null {
    if (this.wielder.main.instance?.id === id) return 'main';
    if (this.wielder.off.instance?.id === id) return 'off';
    return null;
  }

  private equipById(id: string | null, hand: 'main' | 'off'): void {
    if (!id) {
      equip(this.wielder, hand, null, this.wielderCtx);
      return;
    }
    const inst = this.instanceFor(id);
    if (!inst) return;
    // Moving a weapon across hands must not leave a duplicate behind.
    const other = hand === 'main' ? 'off' : 'main';
    if (this.wielder[other].instance === inst) equip(this.wielder, other, null, this.wielderCtx);
    equip(this.wielder, hand, inst, this.wielderCtx);
  }

  private refoldIfHeld(inst: WeaponInstance): void {
    // Free respec means this runs often (docs/04 §7), so it re-folds rather
    // than re-equipping — re-equipping would cancel the node and reset combo.
    if (this.wielder.main.instance === inst) refresh(this.wielder, 'main', this.wielderCtx);
    if (this.wielder.off.instance === inst) refresh(this.wielder, 'off', this.wielderCtx);
  }

  /**
   * `WeaponInstance.modules` is a flat list and the UI addresses sockets as
   * (kind, index). The adapter fills the first free socket of each kind in list
   * order, so the nth module of a kind *is* socket n — that convention is the
   * contract between the two sides and is stated here because it is implicit.
   */
  private installModule(instanceId: string, slot: string, index: number, moduleId: string): void {
    const inst = this.instanceFor(instanceId);
    if (!inst || !this.modules.has(moduleId)) return;
    // A module lives in one socket at a time; installing pulls it out of
    // wherever it was, intact.
    const all = [this.wielder.main.instance, this.wielder.off.instance, ...this.runWeapons];
    for (const other of all) {
      if (!other || other === inst) continue;
      const at = other.modules.indexOf(moduleId);
      if (at >= 0) {
        other.modules.splice(at, 1);
        this.refoldIfHeld(other);
      }
    }
    const sameKind = inst.modules.filter((id) => this.modules.get(id)?.slot === slot);
    const occupant = sameKind[index];
    if (occupant) inst.modules.splice(inst.modules.indexOf(occupant), 1);
    if (!inst.modules.includes(moduleId)) inst.modules.push(moduleId);
    if (!this.runModules.includes(moduleId)) this.runModules.push(moduleId);
    this.refoldIfHeld(inst);
  }

  /**
   * Put a saved module set back on one instance (#17).
   *
   * A module lives in one socket at a time, so this strips each id from every
   * other holding first — the rule `installModule` keeps, applied to a whole
   * set at once rather than one socket at a time, because a snapshot restores a
   * build and not a sequence of clicks.
   */
  private restoreModules(instanceId: string | null, moduleIds: readonly string[]): void {
    if (!instanceId) return;
    const inst = this.instanceFor(instanceId);
    if (!inst) return;
    const all = [this.wielder.main.instance, this.wielder.off.instance, ...this.runWeapons];
    for (const other of all) {
      if (!other || other === inst) continue;
      let moved = false;
      for (const id of moduleIds) {
        const at = other.modules.indexOf(id);
        if (at >= 0) {
          other.modules.splice(at, 1);
          moved = true;
        }
      }
      if (moved) this.refoldIfHeld(other);
    }
    inst.modules.length = 0;
    for (const id of moduleIds) if (this.modules.has(id)) inst.modules.push(id);
    for (const id of inst.modules) if (!this.runModules.includes(id)) this.runModules.push(id);
    this.refoldIfHeld(inst);
  }

  private removeModule(instanceId: string, slot: string, index: number): void {
    const inst = this.instanceFor(instanceId);
    if (!inst) return;
    const sameKind = inst.modules.filter((id) => this.modules.get(id)?.slot === slot);
    const target = sameKind[index];
    if (!target) return;
    inst.modules.splice(inst.modules.indexOf(target), 1);
    // Never consumed — it goes back into the holdings, which is the promise the
    // panel prints in its own footer.
    if (!this.runModules.includes(target)) this.runModules.push(target);
    this.refoldIfHeld(inst);
  }

  /**
   * Codex discovery capture (docs/03 §7). The materials codex is what this
   * player has actually touched, so the sample is the cells the body is in —
   * every eighth sim tick, because the answer barely changes between them.
   */
  private matScanTick = 0;
  private sampleMaterials(): void {
    if ((this.matScanTick++ & 7) !== 0) return;
    const w = this.entities;
    const i = this.playerIdx;
    const x0 = Math.round(w.x[i] - w.halfW[i]) - 1;
    const x1 = Math.round(w.x[i] + w.halfW[i]) + 1;
    const y0 = Math.round(w.y[i] - w.halfH[i]) - 1;
    const y1 = Math.round(w.y[i] + w.halfH[i]) + 1;
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) this.uiAdapter.markMaterialSeenId(this.cells.peek(x, y));
    }
  }

  /**
   * Build the on-screen appearance of an equipped weapon: class picks the
   * motion, reach scales it, damage type colours it, and a collision check
   * guarantees it does not match the wielder's own materials.
   */
  private visualFor(i: number, rw: { def: { class: string; archetype: string }; reach: number; weight: number; damageType: number }): WeaponVisual | null {
    if (rw.def.archetype !== 'melee') return null;

    // Cached by value, because `PixelBodySystem.setWeapon` compares by
    // *identity* to decide whether the body is dirty. Returning a fresh object
    // literal every substep therefore marked the player dirty at 120 Hz and
    // defeated the pose cache's early-out entirely — the file's own promise
    // that "a body standing still costs nothing" was false for the one body
    // that is always on screen.
    const key = `${rw.def.class}|${rw.reach}|${rw.weight}|${rw.damageType}|${i}`;
    if (key === this.visualKey) return this.visualCache;
    this.visualKey = key;
    // The element's own material unless the eye cannot separate it from the
    // body swinging it. This used to be `mat === t[0] || t[1] || t[2]` escaping
    // to BRASS — an identity check where the project's bar is a 40/255
    // luminance step, which caught 2 of the 41 collisions in the catalogue and
    // created a 42nd (see `bladeMatForDamage`).
    const e = this.bodies.get(i);
    const mat = bladeMatForDamage(rw.damageType, e ? e.body.tissueMat[0] : undefined);
    const v = visualForClass(String(rw.def.class), rw.reach, rw.weight, mat);
    this.visualCache = v.style === 'none' ? null : v;
    return this.visualCache;
  }

  private spawnPlayer(): MoverState {
    const w = this.entities;
    const e = w.create(Comp.TRANSFORM | Comp.BODY | Comp.SPRITE | Comp.HEALTH | Comp.PLAYER);
    this.player = e;
    const i = e & 0xfffff;
    this.playerIdx = i;

    const arch = getArchetype(this.archetypeId);
    const body = bodyForArchetype(this.archetypeId);
    const comp = compositionIds(body);

    const spawn = this.spawnPointFor();
    w.x[i] = spawn.x;
    w.y[i] = spawn.y;
    // Size comes from the archetype: a Breaker is genuinely wider than a
    // Scavenger, so the two present different platforming problems through the
    // same geometry (docs/02 §3.2).
    w.halfW[i] = body.halfW;
    w.halfH[i] = body.halfH;
    w.maxHp[i] = 100 * arch.survivability.hpMul;
    w.hp[i] = w.maxHp[i];
    w.team[i] = Team.PLAYER;
    w.mass[i] = 1 * (arch.movement.massMul ?? 1);
    w.poise[i] = w.maxPoise[i] = 30 * (arch.survivability.poiseMul ?? 1);

    // The player's composition is the same field enemies use, so the archetype
    // inherits the whole derived stack: what it resists, what shatters it, and
    // what its wounds spray into the world.
    w.coreMat[i] = comp.core;
    for (let k = 0; k < 4; k++) {
      w.compMat[i * 4 + k] = comp.mats[k];
      w.compFrac[i * 4 + k] = comp.fracs[k];
    }
    this.bodies.attach(w, i, body.plan);

    this.camera.snapTo(spawn.x, spawn.y);

    // Movement deltas and innate traversal abilities both come from the
    // archetype — a Floating Relic starts with flight, a Breaker with Breach.
    const tuning = applyModifiers(defaultTuning(), resolveMovement(this.archetypeId));
    let abilities = 0;
    for (const id of arch.innateAbilities) {
      const bit = ABILITY_BY_ID[id];
      if (bit) abilities |= bit;
    }
    // Attunement picks from the setup screen. Already validated against what
    // the track has earned (unlockEffects.startingAbilitiesFor), so this only
    // has to translate ids into the controller's bitmask.
    for (const id of this.startAbilityIds) {
      const bit = ABILITY_BY_ID[id];
      if (bit) abilities |= bit;
    }
    // Dev affordance: `?abilities=all` grants the full set so traversal can be
    // exercised before progression hands them out.
    if (typeof location !== 'undefined' &&
        new URLSearchParams(location.search).get('abilities') === 'all') {
      abilities = 0x1ff;
    }

    // Remembered so a restart resets to the archetype + attunement package
    // rather than keeping abilities the last run found in the world.
    this.baseAbilityMask = abilities;
    this.log(`body: ${arch.name.toLowerCase()}`);
    const mover = createMover(i, tuning, abilities);
    // Issue #185. A body with stances starts in the first one authored
    // (Flesh-machine hybrid: `'organic'`) rather than sitting at the
    // midpoint `tuning` computed above unmodified — the archetype's own
    // comment on `movement` is explicit that the midpoint is a blend point
    // for the stance system to re-apply its delta onto, not a state a body
    // actually stands in.
    const stances = arch.movement.stances;
    if (stances && stances.length > 0) {
      setStance(mover, stances[0], resolveStance(this.archetypeId, stances[0]));
    }
    // Issue #187. Parasite colony (3) and Modular drone swarm (5) spawn their
    // extra bodies here, beside the one `mover` actually drives.
    this.playerFlock = spawnFlockExtras(w, this.bodies, i, this.archetypeId, arch.movement.bodyCount ?? 1);
    return mover;
  }

  /**
   * A confirmed hit, credited to the combatant who threw it — #138.
   *
   * A hit confirm is two unrelated things wearing one name, and fusing them is
   * the whole defect:
   *
   *   - **Mobility belongs to the striker.** `refreshOnHitConfirm` is the hinge
   *     between combat and traversal (docs/02 §2.6, §6): landing a blow gives
   *     the dash and Airstep back, which is what lets an aggressive build cross
   *     a room without touching the floor. It is a *reward for aggression*, so
   *     it is owed to whoever was aggressive. Handed out on every confirm, an
   *     enemy's round landing on you would top your dash up — the mechanic
   *     inverted into a gift from the thing shooting you.
   *   - **Presentation belongs to the target.** The wound spray and the hurt
   *     pose are what a body looks like when something hits it. A hit is a hit
   *     visually, whoever threw it, so these are ungated. Gating them on the
   *     player would delete every wound in the game that the player did not
   *     personally cause, which is the too-tight fix and the reason the test
   *     for this asserts both directions.
   *
   * The camera is the third thing and takes its own predicate rather than
   * riding along with either: it is neither the striker's nor the target's, it
   * is the *viewpoint's*. It shakes when the player is in the exchange at all —
   * striking or struck — and stays still for a fight across the room that the
   * player is only watching. Note the asymmetry that makes the third predicate
   * necessary: the player being struck is exactly the case that must shake and
   * must *not* refresh.
   *
   * `hand` is not a parameter here, and that is a statement rather than an
   * omission. Nothing in either half is hand-shaped. The consumer that would
   * need it — feeding a projectile's confirm back into the *firing hand's*
   * state, the way `wielder.ts` does for melee (`onHit` triggers, hunger, the
   * echo queue) — is not wired for projectiles at all, and wiring it is a
   * different change. Until then the honest thing is to carry the hand
   * truthfully as far as the boundary (`ProjectileHooks.onHitConfirm` now
   * does) and to name nothing beyond it, rather than to invent a `'main'` for a
   * round the off hand fired.
   */
  private creditHitConfirm(owner: number, target: number, result: HitResult): void {
    if (owner === this.playerIdx) refreshOnHitConfirm(this.mover);
    if (this.playerInExchange(owner, target)) this.camera.shake(0.06);
    this.presentHit(target, result);
  }

  /**
   * Is the player *in* this blow — as the one throwing it or the one taking it?
   *
   * This is the camera's predicate and only the camera's. The camera is not the
   * striker's and not the target's; it is the **viewpoint's**, and a viewpoint
   * shakes for the exchange it is standing in and stays still for a fight
   * across the room it is only watching.
   *
   * It is a named method because every blow-landed hook in this file needs
   * exactly this question:
   *
   *   - `creditHitConfirm`, where either side can be the player — the player's
   *     weapon landing, or a reflected round arriving back;
   *   - the enemy-attack hooks (`makeEnemyAttacks`'s `onHit`, `onParried` and
   *     `onBlocked`, in the constructor above), where the striker is always an
   *     enemy, so the same rule collapses to its second disjunct. On the two
   *     defence hooks it collapses further still — `strike()` consults the
   *     wielder only when the target *is* the player — and they ask anyway,
   *     because "the camera belongs to the viewpoint" is one rule and four
   *     places that each spell out their own true-by-construction version of it
   *     are four places that can drift apart.
   *
   * That collapse is the reason to share rather than to inline. Written out at
   * the second call site the surviving half looks like a *different* rule, and
   * the next hand to touch it has no way to see it is the same one — which is
   * the exact mechanism behind #92, #127, #137 and #139, four instances of a
   * selection and a lookup that were meant to agree and quietly stopped. The
   * first disjunct being dead at the enemy hook costs one comparison and buys
   * one place to change the rule; it also stops being dead the moment anything
   * player-owned routes a blow through `strike()`.
   *
   * What it deliberately does **not** cover: the enemy hook used to shake on
   * every target an enemy blow found, and `boxHit` only filters same-team and
   * `NEUTRAL` — so the player's own drones (`Summons.deploy` copies the owner's
   * team) are valid targets, and an enemy hitting a drone across the room shook
   * the player's camera. `noteHurt` on that drone stays ungated: presentation
   * belongs to the body that was hit, whoever hit it. Gating the whole handler
   * is the too-tight fix, and it deletes every hurt pose the player did not
   * personally take.
   */
  private playerInExchange(owner: number, target: number): boolean {
    return owner === this.playerIdx || target === this.playerIdx;
  }

  /**
   * A hit takes matter out of the body and puts it into the world.
   *
   * The wound lands on the side the blow came from, and the spray follows the
   * knockback — so a hit reads as directional, not as a generic puff centred on
   * the target. `target` may be -1: a spear plant confirms against a wall
   * (`wielder.ts`'s `tryVault`), and `PixelBodySystem` ignores indices it has no
   * body for.
   */
  private presentHit(target: number, result: HitResult): void {
    const w2 = this.entities;
    const kx = result.knockX;
    const ky = result.knockY;
    const kl = Math.hypot(kx, ky) || 1;
    const dirX = kx / kl;
    const dirY = ky / kl;
    const sev = blowSeverity(result.damage);
    this.bodies.wound(
      w2, target,
      w2.x[target] - dirX * w2.halfW[target] * 0.6,
      w2.y[target] - dirY * w2.halfH[target] * 0.4,
      sev, this.particles, dirX, dirY,
    );
    this.bodies.noteHurt(target);
  }

  /**
   * The one damage entry point. Everything — weapons, environment, enemy
   * attacks, status ticks — routes through here so that death handling and the
   * cause readout exist in exactly one place.
   */
  damageEntity(idx: number, amount: number, type: number, srcIdx: number, srcMat = 0): void {
    const w = this.entities;
    if (idx < 0 || w.alive[idx] !== 1) return;
    // I-frames are a *combat* grant. Letting them also cover the environment
    // would mean a dash through lava is free, which reads as a bug and would
    // quietly delete the material simulation as a threat (docs/03 §6).
    if (w.iframes[idx] > 0 && srcMat === 0) return;

    // Issue #187. A shared-pool colony (Modular drone swarm) draws every hit,
    // wherever it lands, from the one pool the controlled body carries — so a
    // hit on a flock body becomes a hit on `playerIdx`, and every downstream
    // rule (i-frames, `tryRevive`, `onPlayerDeath`) only ever has to reason
    // about one entity, same as an archetype with `bodyCount: 1`.
    if (idx !== this.playerIdx && this.playerFlockDamageModel(idx) === 'shared-pool') {
      w.flashTime[idx] = 0.08;
      this.damageEntity(this.playerIdx, amount, type, srcIdx, srcMat);
      return;
    }

    w.hp[idx] -= amount;
    w.flashTime[idx] = 0.08;
    if (w.hp[idx] > 0) return;

    if ((w.mask[idx] & Comp.PLAYER) !== 0) {
      if (this.tryRevive(idx)) return;
      this.onPlayerDeath(type, srcIdx, srcMat);
    } else if (this.playerFlock.indexOf(idx) >= 0) {
      // Issue #187. A 'per-body' colony member (Parasite colony) has always
      // had its own HP for free — it is a separate entity with its own `hp`
      // array slot, the same reason a drone dies independently of its owner
      // in `summons.ts`. Losing it here is a partial loss, not a run ender;
      // `respawn-body` below is the run ender's own fallback, for when the
      // *controlled* body is what just died.
      this.removeFlockBody(idx);
    }
    // Enemy death is the AI system's business — it writes the corpse cells
    // back into the world, which is how bodies feed the ecology.
    void type;
  }

  /**
   * `null` for anything that is not one of this run's own bodies — the
   * primary or one of `playerFlock`. `damageEntity` uses this to decide
   * whether a hit needs colony-wide routing at all before it asks what kind.
   */
  private playerFlockDamageModel(idx: number): DamageModel | null {
    if (idx !== this.playerIdx && this.playerFlock.indexOf(idx) < 0) return null;
    return getArchetype(this.archetypeId).survivability.damageModel;
  }

  /** Detach and destroy a lost flock body — the per-body reap `damageEntity` calls. */
  private removeFlockBody(idx: number): void {
    const at = this.playerFlock.indexOf(idx);
    if (at < 0) return;
    this.playerFlock.splice(at, 1);
    this.bodies.detach(idx);
    const w = this.entities;
    if (w.alive[idx] === 1) w.destroy(w.handleAt(idx));
  }

  /**
   * Issue #172/#185/#187. Intercepts a lethal hit for whichever
   * `Survivability.revival` kind the archetype authors, before
   * `onPlayerDeath` ever runs.
   *
   * `'reassemble'` (Bone construct), `'restance'` (Flesh-machine hybrid) and
   * `'respawn-body'` (Parasite colony) all fire today.
   */
  private tryRevive(idx: number): boolean {
    const revival = getArchetype(this.archetypeId).survivability.revival;
    if (revival.kind === 'reassemble') return this.reassemble(idx, revival.note);
    if (revival.kind === 'restance') return this.restance(idx, revival.note);
    if (revival.kind === 'respawn-body') return this.respawnBody(idx, revival.note);
    return false;
  }

  /**
   * Issue #187. Promotes a surviving flock body to `playerIdx` in place of
   * the one that just died — "falls back to a remaining body", the acceptance
   * criterion this whole issue was blocked on. No HP fraction: see
   * `RESPAWN_BODY_IFRAMES`'s own comment for why the survivor keeps whatever
   * HP it already had rather than being clamped like a rebuilt corpse.
   */
  private respawnBody(idx: number, note: string): boolean {
    if (this.run.revival.chargesRemaining <= 0) return false;
    const survivor = this.playerFlock.find((j) => this.entities.alive[j] === 1 && this.entities.hp[j] > 0);
    if (survivor === undefined) return false;
    this.run.revival.chargesRemaining--;

    const w = this.entities;
    this.playerFlock.splice(this.playerFlock.indexOf(survivor), 1);
    this.bodies.detach(idx);
    w.destroy(w.handleAt(idx));

    w.mask[survivor] |= Comp.PLAYER;
    w.iframes[survivor] = Math.max(w.iframes[survivor], RESPAWN_BODY_IFRAMES);

    this.playerIdx = survivor;
    this.player = w.handleAt(survivor);
    this.mover.entity = survivor;
    this.camera.snapTo(w.x[survivor], w.y[survivor]);

    this.announce('A body remains', { kind: 'unlock', note, log: 'a remaining body took over after a lethal hit' });
    return true;
  }

  private reassemble(idx: number, note: string): boolean {
    if (this.run.revival.chargesRemaining <= 0) return false;
    this.run.revival.chargesRemaining--;

    const w = this.entities;
    // The archetype card's own number (docs/03's table, and the authored
    // `note`): "Reassembles at 30% HP after a lethal hit." No numeric field
    // on `Revival` carries this — `'reassemble'` has exactly one archetype
    // today, so a kind-specific constant here is the honest amount of
    // structure, not a generic `reviveFraction` field with one caller.
    w.hp[idx] = Math.max(1, Math.round(w.maxHp[idx] * REASSEMBLE_HP_FRACTION));
    // A beat of invulnerability so the hit that triggered this cannot also
    // be the hit that undoes it — the same protection ordinary i-frames
    // already grant after a landed hit, extended here because reassembly
    // is a much bigger swing than a normal combat exchange.
    w.iframes[idx] = Math.max(w.iframes[idx], REASSEMBLE_IFRAMES);
    this.announce('Reassembled', { kind: 'unlock', note, log: 'reassembled after a lethal hit' });
    return true;
  }

  /**
   * Issue #172/#185. Flesh-machine hybrid's own kind: forces the *other*
   * stance (organic ↔ machine) via `entity/movement.ts`'s `setStance`,
   * rather than checking whether the destination stance is actually safer
   * against whatever just landed the lethal hit — the card's own promise is
   * a stance swap, unconditionally, not a smart one.
   */
  private restance(idx: number, note: string): boolean {
    if (this.run.revival.chargesRemaining <= 0) return false;
    const stances = getArchetype(this.archetypeId).movement.stances;
    // Only Flesh-machine hybrid authors `kind: 'restance'` and it always
    // authors two stances — a `restance` archetype with fewer than two has
    // nothing to swap into, and that is a content error, not a state this
    // call should paper over by reviving into the same stance.
    if (!stances || stances.length < 2) return false;
    this.run.revival.chargesRemaining--;

    const next = stances.find((s) => s !== this.mover.stanceId) ?? stances[0];
    setStance(this.mover, next, resolveStance(this.archetypeId, next));

    const w = this.entities;
    w.hp[idx] = Math.max(1, Math.round(w.maxHp[idx] * RESTANCE_HP_FRACTION));
    w.iframes[idx] = Math.max(w.iframes[idx], RESTANCE_IFRAMES);
    this.announce('Restanced', { kind: 'unlock', note, log: `restanced into ${next} after a lethal hit` });
    return true;
  }

  private onPlayerDeath(type: number, srcIdx: number, srcMat = 0): void {
    // The run can only end once. With the death screen up the simulation keeps
    // running (docs/23 §5), so the lava that killed the player goes on dealing
    // damage to a body whose `alive` flag is never cleared — and every tick
    // would call this again, banking the run, paying its Ash and incrementing
    // `stats.deaths` once per frame. Nothing before this change needed the
    // guard, because the old path restored full HP in the same call stack.
    if (this.run.outcome !== null) return;
    const w = this.entities;
    // Name the full cause chain. docs/06 §4 and docs/08 §6 converge on this:
    // the difference between a player blaming the game and blaming themselves
    // is almost entirely whether they can see why they died. "Killed by the
    // world" names neither the material nor the chain, so a death to matter
    // now names the matter.
    const killer =
      srcIdx >= 0 && w.alive[srcIdx] === 1 && w.aiKind[srcIdx] > 0
        ? this.enemies.byKind(w.aiKind[srcIdx])?.name ?? 'something'
        : srcMat > 0
          ? materialDef(srcMat).label
          : 'the world';
    const killerId =
      srcIdx >= 0 && w.alive[srcIdx] === 1 && w.aiKind[srcIdx] > 0
        ? this.enemies.byKind(w.aiKind[srcIdx])?.id ?? null
        : null;
    const cause: DeathCause = {
      agent: killer,
      agentId: killerId,
      damage: DAMAGE_TYPE_NAMES[type] ?? 'physical',
      material: srcMat > 0 ? materialDef(srcMat).label : null,
    };
    // The same two strings as before, now spelled in one place. The record
    // carries the damage type; the alert does not — you need to know *what*
    // killed you now, and *how* when you read back.
    this.announce(causeHeadline(cause), { kind: 'warn', log: causeSentence(cause) });
    this.finishRun('death', killerId, cause);
  }

  /**
   * The event ticker — docs/06 §4.
   *
   * The Noita postmortem's argument is that this is worth more than extra
   * simulation: their wet-resists-ignition rule was broken for two months and
   * nobody noticed, because nothing ever said so. That failure was reproduced
   * here almost exactly — `onIgnite` was wired to an empty body, so ignition,
   * the most common state change in the game, was silent, and the reaction
   * lines were gated on a discovery set that outlives the run, so each one
   * printed once per browser session, ever.
   *
   * The split that resolves it: the *codex* is once-ever, the *ticker* is
   * always. Discovery-gating the sentence was the wrong instinct.
   */
  private wireEvents(): void {
    this.thermal.onIgnite = (x, y, mat) => {
      // A settled crate that catches fire has to come back into the active set
      // or it burns to ash in the grid with the body none the wiser. This and
      // `cells.onCellDamaged` are the only two readers of the sleeping-body
      // ownership map, and both fire once per cell rather than once per tick.
      this.rigid.noteCellDisturbed(x, y);
      // Watching a thing burn counts as observing it, even from a distance.
      this.uiAdapter.markMaterialSeenId(mat);
      this.logMaterialEvent(`${materialDef(mat).label.toLowerCase()} ignites`);
    };
    this.pressure.onRupture = (e) => {
      this.camera.shake(0.5);
      this.particles.scatter(e.x, e.y, 6, 260);
      this.log('pressure ruptured a wall');
    };
    this.supports.onFail = (label, x, y) => {
      this.camera.shake(0.35);
      this.particles.scatter(x, y, 4, 180);
      // docs/06 §4 — a ceiling that collapses silently is indistinguishable
      // from a bug. The label is the situation's own line.
      this.log(label);
    };
    this.reactions.onDiscovery = (event, x, y) => {
      const line = event.replace(/_/g, ' ');
      // The codex banks first observations; the ticker narrates every one.
      if (recordDiscovery(this.run, event)) this.progression.recordDiscovery(event);
      // docs/03 §7 — photograph the moment, not the recipe. Capped and measured
      // in uiAdapter (THUMB_CAP); a no-op once the budget is full.
      this.uiAdapter.captureReactionThumb(this.cells, event, x, y);
      this.logMaterialEvent(line);
    };
  }

  /**
   * Rate-limited ticker line.
   *
   * Material events fire per cell, so an unthrottled fire is thousands of
   * identical lines a second. Collapsing repeats inside a short window keeps
   * the sentence without turning the ticker into a flood — the player needs to
   * read "oil ignites" once, not once per cell.
   */
  private lastMaterialLine = '';
  private lastMaterialAt = -1;
  private logMaterialEvent(line: string): void {
    if (line === this.lastMaterialLine && this.time - this.lastMaterialAt < 1.5) return;
    this.lastMaterialLine = line;
    this.lastMaterialAt = this.time;
    this.log(line);
  }

  log(msg: string): void {
    this.events.push(msg);
    if (this.events.length > 64) this.events.shift();
    this.eventSeq++;
  }

  /**
   * A line the player should *look up* for, and also find in the log.
   *
   * The run log and the toasts are one feed in two registers — the toast is
   * loud and expires, the log line is quiet and stays. They now share a screen
   * region (`ui/hudLayout.ts`), which is what stopped them printing through
   * each other, but seven call sites were still emitting both **verbatim**:
   * `salvaged X` beside `X`, `killed by X (type)` beside `Killed by X`,
   * `entered X` beside `X — hazard`, and four more. The player read the same
   * sentence twice, in two places, at two volumes.
   *
   * Routing both through one call makes the duplicate unrepresentable rather
   * than something to notice in a screenshot — and it makes "every toast is
   * also in the log, exactly once" a property a test can assert.
   *
   * `log()` remains for the ~24 line types that are log-only: material
   * reactions, altar rerolls, pressure ruptures, rest nodes, banking, save
   * failures. docs/06 §4 argues those sentences are the highest-value feedback
   * in the game, and none of them wants to interrupt.
   *
   * Deliberately **not** de-duplicated at the UI layer by comparing text within
   * a tick. That would eventually swallow a real line that happened to repeat.
   */
  announce(
    line: string,
    opts: {
      /** Toast styling. `warn` for a hazard, `unlock` for a gain. */
      kind?: ToastKind;
      /** Sub-text inside the toast — the hazard sentence, not a second line. */
      note?: string;
      /**
       * A *different* wording for the log, when the record wants detail the
       * alert should not carry — `killed by X (acid)` against `Killed by X`.
       *
       * Defaults to `line`, so the two agree unless a caller deliberately
       * disagrees. That default is the point: divergence becomes a decision
       * somebody made rather than two call sites drifting.
       */
      log?: string;
      /**
       * A reputation swing to show instead of a plain note: both halves of the
       * opposed pair and the signed movement. `UIRoot.factionToast` composes
       * the sentence and raises *nothing* when the swing moved nothing (#70).
       */
      swing?: { gained: string; lost: string; delta: number };
    } = {},
  ): void {
    this.log(opts.log ?? line);
    if (!opts.swing) this.ui.toast(line, opts.kind ?? 'unlock', opts.note ?? '');
    else this.ui.factionToast(line, opts.swing.gained, opts.swing.lost, opts.swing.delta);
  }

  /**
   * End the run, bank what was earned, and start a fresh one.
   *
   * Death is progress here, not just a reset: the Lineage track advances toward
   * the archetype associated with whatever killed you (docs/05 §4). That is why
   * the cause has to be identified before this is called.
   */
  /** End, bank, persist, and raise the postmortem. The run stops here. */
  private finishRun(
    kind: RunOutcomeKind,
    killedBy: string | null,
    cause: DeathCause | null,
  ): void {
    endRun(this.run, kind, killedBy, undefined, cause);
    // The corpse. `riteFor` returns null for `extract` and `abandon` -- docs/23
    // §7.7 -- so that is the whole of the suppression, and there is deliberately
    // no `kind === 'death'` here to drift out of step with it. `beginDeath`
    // refuses a body that is already dying, which is the guard for the lava
    // that goes on damaging the corpse under the panel (§7.5).
    const rite = riteFor(this.run.outcome, this.entities, this.playerIdx);
    if (rite !== null) this.bodies.beginDeath(this.playerIdx, rite);
    const report = bankRun(this.run, this.progression);

    if (report.lineage.archetype && report.lineage.amount > 0) {
      this.log(`lineage +${report.lineage.amount} toward ${report.lineage.archetype}`);
    }
    if (report.ash > 0) this.log(`banked ${report.ash} ash`);
    if (report.scripLost > 0) this.log(`lost ${report.scripLost} scrip`);
    if (report.ashCarried > 0) this.log(`courier carried ${report.ashCarried} ash through death`);
    if (report.mapEchoed) this.log('the route is remembered for this seed');
    const unlocks: string[] = [];
    for (const ev of this.progression.pendingUnlocks()) {
      unlocks.push(ev.id);
      this.announce(`Unlocked — ${ev.id}`, { log: `unlocked ${ev.id}` });
    }

    // A failed save must never take the game down with it — SaveSystem reports
    // rather than throws, so this is just a log.
    const res = this.persistProfileWithResult();
    if (res.error !== 'none') this.log(`save failed (${res.error})`);

    // Built once. `buildGameOverVM` is pure, and `Game.events` is snapshotted
    // with a slice because the ring buffer keeps filling behind the screen.
    this.ui.setGameOver(buildGameOverVM({
      run: this.run,
      report,
      log: [...this.events],
      unlocks,
      labels: {
        biome: (id: string) => biomeById(id)?.name,
        enemy: (id: string) => this.enemies.get(id)?.name,
        ability: (id: string) => ABILITY_TEXT[id]?.name,
      },
      carried: {
        mainHand: this.wielder.main.weapon?.def.name ?? null,
        offHand: this.wielder.off.weapon?.def.name ?? null,
        weapons: this.runWeapons.map((w) => this.weapons.get(w.defId)?.name ?? w.defId),
        modules: [...this.runModules],
      },
      hasQuitToTitle: this.onQuitToTitle !== null,
    }));
    this.ui.setScreen('gameover');
  }

  /** Reset and respawn. Called by the death screen's Continue, and only there. */
  private startNextRun(): void {
    // Fresh run on a new seed, and the world genuinely regenerates around it.
    // Reseeding without clearing the cache would be a no-op: resident chunks
    // are the old world and answer for the new one until they are evicted.
    this.seed = (this.seed * 1664525 + 1013904223) >>> 0;
    if (this.worldGen) {
      this.worldGen.reseed(this.seed);
      this.cells.clear();
      // Groups name cells in the old world. They re-register as the new one
      // generates.
      this.supports.clear();
      // Bodies name cells in the old world too, and unlike support groups
      // nothing re-registers them: a surviving body would keep a sleeping-cell
      // index pointing into a world that no longer exists.
      this.rigid.clear();
      // And the ledger with them: its records name sites in the old seed's
      // world, and its site cache names chunks that will never come back.
      this.runBodies.clear();
      this.runTerrain.clear();
    }

    // `worldMode` and `archetype` are passed because they were not, and the
    // omission was invisible until something read the run back (#51). The
    // world generator is *reseeded*, not rebuilt, so it keeps the mode the run
    // was started with — but `createRunState` defaults to `'layered'` and
    // `'duelist'`, so every run after the first recorded a world and a body it
    // was not being played in. Nothing read those fields, so nothing noticed;
    // Continue reads both, and would have rebuilt a layered world around a
    // blended cave. Same class of drift as the ids in docs/13 §2, and the same
    // fix: say the value rather than let a default say it for you.
    this.run = createRunState({
      seed: this.seed,
      worldMode: this.run.worldMode,
      archetype: this.archetypeId,
      progression: this.progression,
      kit: this.kit.id,
    });
    this.runBodies.bind(this.run);
    // #118. Only a `world` scene adopts the run's terrain log. The prologue and
    // the test room are authored spaces whose generators ignore `run.seed`, and
    // the prologue shares the real save -- so a dig there is recorded onto a run
    // Continue offers as a *world* run, and replayed as a hole in a procedural
    // cave the player never dug. The seed guard cannot see it: same run, same
    // seed.
    if (this.worldGen) this.runTerrain.bind(this.run);
    // The surface log binds beside the run log and under the same #118 guard,
    // with the difference surfaceTerrain.ts's bind names: the run is new and
    // the profile is not, so this replays the same marks into the new world.
    if (this.worldGen) this.surfaceTerrain.bind(this.profile);
    // The same clock dresses the surface: which enemies are out, and what the
    // daylight is doing. Applied wherever the profile binds, so an advanced
    // surfaceSeed re-dresses the world the same moment it heals it.
    if (this.worldGen) {
      const day = surfaceDressing(this.profile.surfaceSeed);
      this.spawner.setSurfaceRoster(day.roster);
      setSurfaceAmbientMul(day.ambient);
    }
    // Found abilities do not carry over — that is the whole shape of the run.
    // Attunement is the only channel that survives a death, and it is spent at
    // the setup screen, not here.
    this.caches.length = 0;
    this.visits.clear();
    this.sanctumPool = null;
    this.restNodes.length = 0;
    this.vendors.length = 0;
    this.vendorInRange = null;
    this.restBands.clear();
    this.cacheInRange = null;
    this.runWeapons.length = 0;
    this.runModules.length = 0;
    // Re-issue the kit: the wielder still holds last run's instances, so
    // clearing the holdings alone left dead weapons in both hands.
    equip(this.wielder, 'main', null, this.wielderCtx);
    equip(this.wielder, 'off', null, this.wielderCtx);
    this.equipKit();
    this.mover.abilities = this.baseAbilityMask;
    const w = this.entities;
    const arch = getArchetype(this.archetypeId);
    w.hp[this.playerIdx] = w.maxHp[this.playerIdx];
    // The run is over; so are its afflictions.
    //
    // Nothing else zeroes these. Enemies are immune because `World.create`'s
    // `resetSlot` clears the status slots when a slot is reused — but the
    // player entity is never recreated, so it carried them across the boundary.
    // A player killed by the Spore Bloom respawned still holding POISONED x5
    // and INFECTED x4 and lost 18.5 HP of a fresh 100 standing in empty air,
    // with the HUD reporting "Killed by the world" and no visible cause (#53).
    //
    // `clearStatuses` was exported and had zero call sites anywhere in `src/`.
    clearStatuses(w, this.playerIdx);
    this.spawner.clear(w);
    this.summons.clear(w);
    // Issue #187. Whatever survived (or didn't) of last run's colony does not
    // carry over — a fresh run gets the archetype's full `bodyCount` back,
    // same as HP and abilities just above.
    for (const j of this.playerFlock) if (w.alive[j] === 1) w.destroy(w.handleAt(j));
    this.playerFlock = [];
    this.bodies.clear();
    // The body is the archetype's, not a generic humanoid — this used to
    // discard `bodyForArchetype`, so a Floating Relic became a humanoid raster
    // on its second run while its tuning and composition stayed Relic.
    this.bodies.attach(w, this.playerIdx, bodyForArchetype(this.archetypeId).plan);

    const spawn = this.spawnPointFor();
    w.x[this.playerIdx] = spawn.x;
    w.y[this.playerIdx] = spawn.y;
    w.vx[this.playerIdx] = w.vy[this.playerIdx] = 0;
    this.playerFlock = spawnFlockExtras(
      w, this.bodies, this.playerIdx, this.archetypeId, arch.movement.bodyCount ?? 1,
    );
    this.camera.snapTo(spawn.x, spawn.y);

    // Generate around the *new* spawn before the altar reads the floor, and
    // raise the altar only after the player has moved. The old order raised it
    // from the corpse's coordinates, so every run after the first carved its
    // one weapon source into terrain hundreds of cells from where you stood.
    this.cells.updateActive(
      this.camera.renderX, this.camera.renderY, this.camera.viewW, this.camera.viewH,
    );
    this.groundPlayer();
  }

  /**
   * Put the player on a floor.
   *
   * `WorldGen.spawnPoint()` is a lattice coordinate with no ground check, so
   * the player can be dropped over a shaft. That is not merely untidy: the
   * altar is raised at the player's feet, so falling means falling *past* the
   * one guaranteed structure in the run, with no traversal ability yet to climb
   * back. Scan down for a surface; if there is none within a screen, stay put
   * and let the altar's terrace provide the floor.
   */
  /**
   * Write the profile and the run now — issue #51.
   *
   * The shell's hook, called immediately before `destroy()`. Without it,
   * quitting to the title left the run on disk as of the last death, stash
   * withdrawal or craft, which for most quits is "never" — and a Continue
   * button offering that run would be a button that lies. `meta/save.ts`'s
   * `Autosaver` is the real answer and still has no call site anywhere in
   * `src/`; this is the one write that makes Continue honest in the meantime.
   */
  persist(): void {
    this.persistProfileWithResult();
  }

  /** The profile write, so a stash or craft mutation is durable immediately. */
  private persistProfile(): void {
    this.persistProfileWithResult();
  }

  private persistProfileWithResult(): ReturnType<SaveSystem['save']> {
    this.profile.progression = this.progression.state;
    this.profile.settings.codexThumbs = this.uiAdapter.exportThumbs();
    return this.save.save(this.profile, this.run);
  }

  /** Where this scene starts the player. The prologue is authored, not generated. */
  private spawnPointFor(): { x: number; y: number } {
    if (this.scene === 'prologue') return PROLOGUE_SPAWN;
    if (!this.worldGen) return testRoomSpawn();
    // docs/27 §3.2 — an authored place is paid for once per profile, not once
    // per run. A returning profile begins at the cave mouth; the *first* run of
    // a profile still begins at the surface spawn, because that walk is what
    // teaches the frame: the tree that refuses you upward, the mountain that
    // refuses you across, and the one opening that does not.
    if (this.progression.state.lifetime.runs > 0) {
      const m = this.worldGen.descentMouth();
      return { x: m.x, y: m.y - 12 };
    }
    return this.worldGen.spawnPoint();
  }

  private groundPlayer(): void {
    const w = this.entities;
    const i = this.playerIdx;
    const x = Math.round(w.x[i]);
    const halfH = w.halfH[i];
    for (let d = 0; d < 240; d++) {
      const y = Math.round(w.y[i]) + d;
      if (this.cells.isSolid(x, y + Math.ceil(halfH) + 1)) {
        w.y[i] = y;
        this.camera.snapTo(w.x[i], w.y[i]);
        return;
      }
    }
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    const tick = (now: number) => {
      if (!this.running) return;
      this.frame(now);
      this.rafHandle = requestAnimationFrame(tick);
    };
    this.rafHandle = requestAnimationFrame(tick);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafHandle);
  }

  /**
   * Open a panel from the pad — issue #78.
   *
   * `DEFAULT_PAD_BINDINGS` has produced `inventory` (Back), `pause` (Start) and
   * `map` (R3) since #67 and **nothing consumed any of them**, which is half of
   * why a controller could play a run and not reach a menu. The other half is
   * fixed in `src/ui/menuPad.ts`, which drives a panel once it is open.
   *
   * Three properties this relies on, each load-bearing:
   *
   *   - **It cannot double-fire with the keyboard.** `UIRoot`'s own keydown
   *     handler runs in the capture phase and calls `stopPropagation()` on
   *     Tab / C / M / Escape, so `Input`'s window-level listener never sees any
   *     of the four. These three actions are therefore reachable only from a
   *     pad, and pressing Escape still goes down exactly the path it always did.
   *   - **It stops at the panel boundary.** Once a screen is open the pad
   *     belongs to `UIRoot`'s pump, and `Input` is disabled anyway.
   *   - **The prologue keeps Start.** `updatePrologue` skips on a *held* `pause`
   *     and this consumes the *press*, so without the guard the skip-hold would
   *     be shadowed by a pause menu opening on frame one of the hold.
   *
   * (Noticed while wiring this and NOT fixed here, because it is not #78: the
   * keyboard's prologue skip is already dead for the same capture-phase reason
   * — `Escape` never reaches `Input`, so `held('pause')` can only ever be true
   * from a pad. Filed separately.)
   */
  private pollMenuButtons(): void {
    if (this.ui.currentScreen !== 'none') return;
    // #82: only while the hold is actually on offer. Guarding the whole scene
    // left Start doing nothing at all on a first run, which is the one run
    // where a new player is most likely to press it.
    if (this.prologueSkipOffered) return;
    // One per frame: two panels opening on one frame is a panel the player
    // never sees, and `consume` is what makes the press single-use. 34 ms is
    // the one-frame window `Input.pressed` uses — a menu open is not an action
    // that wants buffering, so it is deliberately not longer.
    if (this.input.consume('inventory', 34)) this.ui.setScreen('loadout');
    else if (this.input.consume('map', 34)) this.ui.setScreen('map');
    else if (this.input.consume('pause', 34)) this.ui.setScreen('pause');
  }

  private frame(now: number): void {
    profiler.begin('frame');
    let dt = (now - this.lastTime) / 1000;
    this.lastTime = now;
    if (dt > MAX_CATCHUP) dt = MAX_CATCHUP;
    this.time += dt;

    this.input.update(dt);
    this.glc.resize();

    // #78: the pad's three menu buttons. Nothing read them, which is why a
    // controller could reach a run and not a menu.
    this.pollMenuButtons();

    if (!this.paused) {
      this.moveAccum += dt;
      let moveSteps = 0;
      while (this.moveAccum >= MOVE_DT && moveSteps < 8) {
        this.stepMovement(MOVE_DT);
        this.moveAccum -= MOVE_DT;
        moveSteps++;
      }

      this.handlePainting();

      this.simAccum += dt;
      let simSteps = 0;
      while (this.simAccum >= SIM_DT && simSteps < 4) {
        this.stepSimulation(SIM_DT);
        this.simAccum -= SIM_DT;
        simSteps++;
      }
    }

    this.render(dt);
    profiler.end('frame');
    profiler.endFrame();
  }

  private stepMovement(dt: number): void {
    profiler.begin('entities');
    const w = this.entities;
    const i = this.playerIdx;

    // Hitstop freezes the actor but must not eat buffered input — the buffer
    // keeps filling during freeze so a combo entered during hitstop lands.
    // The `if` is the freeze gate only. The wielder drains the player's
    // hitstop and enemyAI drains the enemies'; decrementing here as well made
    // every player hitstop half its authored length.
    if (w.hitstop[i] > 0) {
      // frozen this substep
    } else {
      // The input clock is the *simulation's*, not the wall's. Advancing it
      // here — inside the not-frozen branch, on the movement substep — is what
      // makes docs/02 §4's "hitstop does not consume buffered input" true: real
      // time keeps running during a freeze, and a jump pressed mid-hitstop used
      // to expire before the landing it was buffered for.
      this.input.advanceClock(dt);
      // Combat's mobility cost, read before the controller runs so movement
      // sees this substep's state and not last one's (docs/04 §8).
      const cm = this.combatMove;
      cm.attacking = isAttacking(this.wielder);
      cm.drag = attackDragOf(this.wielder);
      cm.jumpCancel = this.wielder.canJumpCancel;
      cm.staggered = isStaggered(w, i);
      stepMover(this.mover, w, this.input, dt, this.moveCtx);
      // Issue #175, split from #159/#174. `applyDrowning` owns the ladder;
      // this is only the "is the hazard present" decision — past the
      // submersion threshold, and this archetype's `Survivability.drowns`.
      // `tickStatuses` (already running every tick, `enemyAI.ts`) drains and
      // damages what gets applied. Stepping out of the water simply stops
      // feeding the ladder; nothing here removes the status early, the same
      // as every other ambient hazard.
      applyDrowning(
        w, i,
        w.submersion[i] > DEEP_WATER_SUBMERSION && getArchetype(this.archetypeId).survivability.drowns,
        dt,
      );
      // Abilities run after the base controller: they layer on top of movement
      // rather than replacing it (docs/02 §3 rule 2 — compose, never bypass).
      updateAbilities(this.mover, w, i, this.input, dt, this.abilityCtx);
      // #122: aims the field and holds the root. Cell work is on the sim tick.
      stepGather(this.gather, this.mover, w, i, this.input, dt, this.gatherCtx);
      displaceCells(w, i, this.cells, this.particles);
    }
    profiler.end('entities');

    // The altar runs before combat: claiming must consume the interact press
    // before anything else looks at it.
    if (this.altar) {
      this.altar.update(dt, w.x[i], w.y[i]);
      if (this.altar.inRange && this.input.consume('interact', 160)) {
        this.altar.claim();
      }
    }
    this.updateCaches(w.x[i], w.y[i]);
    this.updateSanctum(w.x[i], w.y[i]);
    this.updateVendors(w.x[i], w.y[i], dt);
    this.updatePrologue(w.x[i], w.y[i], dt);
    // Rest nodes are optional in-band finds (#6) — the workbench, as opposed to
    // the sanctum, which is the guaranteed gate. Standing at one is what makes
    // the stash, reforge and crafting reachable.
    const atRest = restNodeAt(this.restNodes, w.x[i], w.y[i]) !== null;
    if (atRest !== this.atRestNode) {
      this.atRestNode = atRest;
      this.stashView = new Stash(this.progression).view(atRest);
      this.craftDirty = true;
      if (atRest) {
        const svc = restServices(this.progression.unlockedRestNodes());
        const names = [
          svc.stash ? 'stash' : null,
          svc.mend ? 'mend' : null,
          svc.altar ? 'attune' : null,
        ].filter(Boolean);
        this.log(`rest node — ${names.join(' · ')}`);
      }
    }
    // #158: `ash.rest.mend` and `ash.rest.altar` each completed every step of
    // their purchase chain except the one that mattered. Every frame the
    // player is at a rest node, not only on the entry transition above,
    // because a run held here on purpose (reading the shop, waiting out a
    // cooldown) must be able to press interact the moment it needs either —
    // not only once on arrival. One `interact` press does one thing: mend
    // takes priority while there is HP to restore, since it is the more
    // urgent of the two and the altar is not going anywhere.
    if (atRest) {
      const svc = restServices(this.progression.unlockedRestNodes());
      if (svc.mend && w.hp[i] < w.maxHp[i]) {
        if (this.input.consume('interact', 160)) {
          w.hp[i] = w.maxHp[i];
          this.log('mended — full health');
        }
      } else if (svc.altar) {
        // Full heal, not a fraction (mend's own reasoning): unlike the
        // sanctum pool (a single free draught, `sanctum.ts`'s own docblock),
        // both were bought with ash and the bench is revisitable, so there is
        // no scarcity left to model.
        //
        // "Swap a run-start traversal ability" (progression.ts's shop copy):
        // release candidates are `traversal.granted` minus the archetype's
        // own innates (the same filter #161's `onStart` applies before ever
        // storing a pick, so this never offers to release something that was
        // never a slot in the first place); the incoming ability is drawn
        // from what the player has attuned but is not currently holding —
        // the backlog attunement built up beyond however many slots were
        // bought, which the altar is what turns into play without needing a
        // fourth slot. `grantAbility` lands it in `traversal.found`, exactly
        // where an ability picked up any other way in the run lives — a
        // swap is the player gaining an ability during play, not a rewrite
        // of what the run started with.
        if (this.input.consume('interact', 160)) {
          const innate = new Set<string>(getArchetype(this.archetypeId).innateAbilities);
          const releasable = this.run.traversal.granted.filter((id) => !innate.has(id));
          const held = new Set(runAbilities(this.run));
          const incoming = this.progression.attunedAbilities().filter((id) => !held.has(id));
          if (releasable.length === 0 || incoming.length === 0) {
            this.log('attunement altar — nothing to swap');
          } else {
            const released = releasable[this.rng.int(0, releasable.length - 1)];
            const gained = incoming[this.rng.int(0, incoming.length - 1)];
            this.run.traversal.granted = this.run.traversal.granted.filter((id) => id !== released);
            grantAbility(this.run, gained);
            this.log(`attuned — ${released} for ${gained}`);
          }
        }
      }
    }

    profiler.begin('combat');
    // Combat runs at the movement rate, not the sim rate: an attack that
    // resolves a frame late is felt, and the whole point of the 120 Hz substep
    // is that input-to-response stays under 33 ms (docs/05 §7).
    updateWielder(this.wielder, w, i, this.cells, this.input, dt, this.wielderCtx);
    // Parry is its own verb (docs/04 §4). Consumed before the projectile step
    // so a round arriving this substep can be met.
    if (this.input.consume('parry', 120)) startParry(this.wielder);
    // Summons fly before rounds move, so a drone that just fired has already
    // taken its position for the frame.
    this.summons.step(w, this.cells, this.projectiles, dt);
    this.projectiles.step(w, this.cells, dt, this.rng, {
      // #138: the pool is one path for both sides, so `owner` is the only thing
      // that says whose round this was — and this used to read it and throw it
      // away (`void owner`) before crediting the player's wielder for it. A
      // discard reads as deliberate, so nothing flagged it; only driving both
      // actors can see it. `creditHitConfirm` splits the halves that the
      // wielder's own hook can afford to fuse.
      //
      // `hand` is carried now rather than fabricated — the literal `'main'` it
      // replaces was a lie about an off-hand round that nothing had yet read.
      // Dropped here for the reason `creditHitConfirm` gives.
      onHitConfirm: (owner, _hand, target, result) =>
        this.creditHitConfirm(owner, target, result),
      damageEntity: (idx, amount, type, srcIdx) =>
        this.damageEntity(idx, amount, type, srcIdx),
      particles: this.particles,
    });
    // Swing animation triggers on the *attempt*, not on the hit. A whiff that
    // plays no animation reads as the input having been dropped.
    //
    // #137: on the attempt, and on nothing else. The predicate here used to be
    // "a hand stopped being idle", which is a superset of "a hand started" —
    // `reloading` and `charging` are non-idle — so one press that emptied a
    // magazine announced two swings, and a charge weapon announced its swing at
    // the moment the hold opened and stayed silent for the blow.
    //
    // #142: and the edge is now **taken** rather than re-derived here. This
    // poll runs after `updateWielder`, so a hand it ticked has already left the
    // `timer === 0` instant that the old predicate looked for. That is harmless
    // for a node `updateWielder` itself started — the poll follows it in the
    // same pass — and it silently dropped every node started from anywhere
    // else. The counter a parry opens is started on the **enemy step**, so it
    // ran, dealt its damage, and was never drawn: the reward for the read was
    // invisible. `takeSwingStart` consumes a latch `startNode` and `startParry`
    // raise, so the edge survives until somebody asks instead of until the next
    // tick.
    //
    // Both calls must happen unconditionally, and that is the price of a latch:
    // a `||` that short-circuited would leave the off hand's flag standing for
    // the next substep to find, so two hands starting together would announce
    // twice, a substep apart — #137's symptom through #142's mechanism. Held by
    // `pressDispatch.test.ts` §3's "both hands starting on one substep" row.
    const startedMain = takeSwingStart(this.wielder.main);
    const startedOff = takeSwingStart(this.wielder.off);
    if (startedMain || startedOff) {
      // #92: ask about the hand that is actually swinging. This used to select
      // the hand and then look up `'main'` regardless, so an off-hand-only
      // swing got 0 back — main being idle — and fell to the fallback below.
      // It is now the hand the edge fired on, so selection and lookup cannot be
      // two answers at all. Main wins a tie: one body, one pose.
      const h = startedMain ? this.wielder.main : this.wielder.off;
      const dur = nodeDuration(this.wielder, h.hand) || 0.26;
      // #90: the same hand's *wind-up*, so the swing is drawn gathering while
      // the hitbox is still shut rather than sweeping straight through it. The
      // hitbox does not exist until `nodeWindup` elapses, and the pose used to
      // run one linear ramp across the whole node — so the lead arm had already
      // travelled 18°–57° of its 172° arc before the blow was live. Zero when
      // the node is unknown, which is `noteAttack`'s identity remap: the 0.26 s
      // fallback above has no wind-up and must not invent one. A parry has no
      // node and takes that fallback by design.
      const windup = h.node && h.weapon ? nodeWindup(h) / 1000 : 0;
      // The slot is claimed by the hand that threw the blow, and it is claimed
      // *here*, on the same edge and from the same expression the announcement
      // uses — see the block below for why it cannot be derived instead.
      this.swingHand = h.hand;
      this.bodies.noteAttack(i, dur, windup);
    }
    // ------------------------------------------------------------------ #139
    //
    // A body has **one** weapon slot, and this is the only writer of it. The
    // slot must hold the weapon the *pose* belongs to, because the pose and the
    // slot are drawn as one object: `drawWeapons` stamps `e.visual` along
    // `e.swingAngle`, so whatever is in the slot while `attackT` is running is
    // what the player sees swing. Geometry follows from that for free — a lance
    // visibly outreaches a dagger without anyone authoring the difference.
    //
    // It used to be filled from `drawnHand`, and that is a different question.
    // `drawnHand` answers *which weapon is held* — its docstring says so: the
    // main hand keeps the slot at rest and yields it only while the off hand
    // alone is running a node. The announcement three lines above fires on
    // `startedMain || startedOff`, i.e. on whoever *acted*. Two questions, two
    // answers, one slot: measured through a real `Game` with a hammer in the
    // main hand and the Repeater in the off, an off-hand press ran the gun's
    // node for three frames and then went idle — at which point `drawnHand`
    // handed the slot back to the hammer **while the pose still had 13 ms to
    // run**, and `drawWeapons` stamped 16 quads of hammer. The gun never made a
    // sword appear; the sword was already in the slot and the gun's press made
    // it swing. That is the report, in pixels.
    //
    // So the slot is latched on the edge, and held for the length of the
    // announced swing. Held against `attackT` and not against a copy of `dur`,
    // because `noteAttack` clamps to a 0.08 s floor: a 40 ms node draws for
    // 80 ms, and a latch that expired with the node would hand the slot back
    // mid-pose — the same bug, one clamp smaller. `attackT` is the pose's own
    // clock, so the two cannot disagree by construction.
    //
    // There is no `?? this.wielder.main.weapon` any more. That fallback was a
    // second, quieter copy of the same wrong preference: an empty hand that
    // threw a punch drew the *other* hand's blade. An empty hand draws nothing.
    if ((this.bodies.get(i)?.attackT ?? 0) <= 0) this.swingHand = null;
    const rw = this.wielder[this.swingHand ?? 'main'].weapon;
    this.bodies.setWeapon(i, rw ? this.visualFor(i, rw) : null);
    if (this.wielder.pogoed) pogo(this.mover, w);
    // The spear plants and vaults — a weapon used as traversal (docs/04 §3.2).
    if (this.wielder.vaulted) this.camera.shake(0.08);
    profiler.end('combat');
  }

  private stepSimulation(dt: number): void {
    const cam = this.camera;
    this.cells.tick++;
    // The run's own clock -- #118. Beside `cells.tick++` and not by accident:
    // `RunState.tick` is the key `RunTerrain` coalesces terrain diffs on, so the
    // two counters must step together or "same tick" means nothing. `dt` is the
    // fixed step, so this counts simulated time, and the loop does not run while
    // paused.
    advanceRun(this.run, dt * 1000, 1);
    this.cells.updateActive(cam.renderX, cam.renderY, cam.viewW, cam.viewH);
    // #84, and before everything else: the instantiated set is brought in line
    // with where the player is, so a body seeded this tick is placed by this
    // tick's `writeBack` and a body retired this tick has its cells out of the
    // grid before `cellSim` walks over them.
    this.runBodies.sync(cam.renderX, cam.renderY, cam.viewW, cam.viewH);

    // Before `cellSim`, so a support that failed on the strength of last
    // tick's fire drops its load on this tick rather than leaving it hanging
    // over a plank that is already gone.
    profiler.measure('supports', () => this.supports.step());
    // #42's frame order, and the one place it improves on the talk's. Only
    // `cellSim` needs the body gone — it is the single pass that would treat a
    // crate's wood as loose matter. `writeBack` runs *before* the material
    // passes, so `thermal` and `reactions` below see the crate's real WOOD
    // cells: a wooden crate in a fire burns, chars and structurally fails with
    // no rigid-body-specific code. `RigidBodies` brackets all three of its
    // passes under the `rigid` profiler key itself, the way `cellsim` does.
    this.rigid.lift();
    this.cellSim.step();
    this.rigid.step(dt);
    this.rigid.writeBack();
    // #122, and the position is load-bearing. Between `lift()` and `writeBack()`
    // a rigid body's cells are not in the grid at all, so a field stepped there
    // would haul a crate's WOOD away one cell at a time without ever touching a
    // rigid body. After `writeBack` they are present *and* carry STATIC, which
    // is what the field refuses on. After `cellSim` so this tick's gravity does
    // not undo every inward step; before `thermal`/`reactions` so a lava cell it
    // just moved reacts from where it now is.
    this.attract.step();
    // Costs a `length` check on the 99.99% of ticks where nobody is dying.
    this.bodies.stepDeath(this.entities, this.particles);
    this.particles.step(dt);
    // Every other cell system brackets itself (cellsim/particles/thermal/
    // charge/pressure/light); reactions.ts never has, so its §9 row has never
    // appeared on the F3 overlay and its LOD trip has never fired (#75).
    profiler.measure('reactions', () => this.reactions.step());
    this.thermal.step();
    this.charge.step();
    this.charge.decay();
    this.pressure.step();
    this.light.step();

    // Ecology runs after the cell systems: enemies read a settled world, and
    // shapers' terrain writes land in the next tick's dirty rects.
    const px = this.entities.x[this.playerIdx];
    const py = this.entities.y[this.playerIdx];
    this.spawner.update(
      this.entities, this.cells, px, py,
      // Above the ground line the roster is the surface's own — docs/27 §12
      // item 5 — cut per day by the dressing the profile's surfaceSeed picks.
      (x, y) =>
        this.worldGen
          ? y < 0
            ? ('surface' as BiomeId)
            : (this.worldGen.biomeAt(x, y).id as BiomeId)
          : ('emberworks' as BiomeId),
      this.rng,
    );
    // Enemy bodies are integrated here, and **before** the AI that steers them
    // (#72). Until this line existed no enemy in the game had ever moved:
    // `stepBody` is the one integrator and `stepMover` — its only caller — is
    // the player's. The AI wrote a velocity every tick and nothing read it.
    //
    // The order is not cosmetic. `sweepLunge` (behaviors.ts) resolves a dash
    // over `[x − v·dt, x]`, the ground *behind* the body, so it needs this
    // tick's velocity to have already been applied. Run after `updateEnemies`,
    // that trailing segment would cover travel that has not happened yet —
    // which is the exact "a dash claims ground it has not reached" defect #49
    // was fixed to remove.
    // Bracketed since #75. `stepSimulation` sits inside no begin/end pair, so
    // the body pass — up to `maxLive` collision integrations a tick, §9's
    // 1.2 ms line — was invisible to the profiler, the F3 overlay and LOD.
    // That is why it went unbudgeted: nothing measured it.
    profiler.measure('bodies', () =>
      stepEnemyBodies(this.entities, this.cells, dt, this.enemies),
    );
    profiler.measure('enemyai', () =>
      updateEnemies(this.entities, this.cells, this.playerIdx, dt, this.rng, this.enemyCtx),
    );
    // Issue #187. Falls under real gravity and collides with real terrain on
    // the way toward the controlled body — costs nothing on the ~30
    // archetypes with an empty `playerFlock`.
    if (this.playerFlock.length > 0) {
      stepPlayerFlock(this.entities, this.cells, this.playerIdx, this.playerFlock, dt);
    }
    this.trackLocation(px, py);
    this.sampleMaterials();

    // Dirty rects rotate after every system that could have written cells.
    for (const c of this.cells.activeChunks) c.rotateDirty();
  }

  /**
   * Record where the player is, for the Salvage track and the map.
   *
   * `enterBiome` and `enterRoom` both existed with zero call sites, so
   * `run.biomeOrder` never held anything but `createRunState`'s placeholder and
   * Salvage was pinned at a single award for a biome that does not exist. The
   * room graph was empty for the same reason, which is why the map screen had
   * nothing to draw.
   */
  private trackLocation(px: number, py: number): void {
    if (!this.worldGen) return;
    const b = this.worldGen.biomeAt(px, py);
    if (enterBiome(this.run, b.id)) {
      // The seventh pair. Every biome authors a sentence about its ambient
      // hazard (#27) and arrival is the moment it is actionable, so it rides
      // the arrival toast as sub-text rather than a second toast that would
      // race it — and it is a `warn`, because a hazard is a warning.
      const haz = b.ambientHazard;
      this.announce(haz.label ? `${b.name} — ${haz.label}` : b.name, {
        kind: haz.desc ? 'warn' : 'discovery',
        note: haz.desc ?? '',
        log: `entered ${b.name.toLowerCase()}`,
      });
      this.placeBiomeBoss(b.id, px, py);
      // Ability caches live in sanctums now (#1 phase 4) — placing a second
      // one per biome would hand out two abilities per band.
      // One band's worth of rest nodes, placed off the main path. Bands are
      // ~900 cells (docs/05 §1); `restNodesInBand` owns the lateral offsets.
      const band = Math.floor(py / 900);
      if (!this.restBands.has(band)) {
        this.restBands.add(band);
        // From worldgen, not recomputed: `restNodesInBand`'s default snap is
        // the identity, but the chambers are carved at the lattice-snapped
        // floor. The two diverged by up to 24 cells against a 26-cell claim
        // radius, so the bench sat outside its own room.
        this.restNodes.push(...this.worldGen.restNodes(band));
        // A vendor at some rest nodes, not all — finding one should feel like
        // luck rather than a facility (#11).
        for (const n of this.worldGen.restNodes(band)) {
          const pl = vendorForRestNode(this.run.seed, n);
          if (pl) this.vendors.push(new FactionVendor(pl, this.modules));
        }
      }
    }
    // The map repeats the hazard after the toast expires (#27) — `desc` had no
    // consumer at all before this.
    this.biomeHazard = b.ambientHazard.desc;
    const cx = Math.floor(px) >> CHUNK_SHIFT;
    const cy = Math.floor(py) >> CHUNK_SHIFT;
    enterRoom(this.run, `${cx},${cy}`);
    // Issue #172. A room-scoped revival charge refills the moment the room
    // actually changes — same call site `enterRoom` already runs from,
    // right after it, so the two never disagree about "which room."
    refillRoomRevival(this.run, getArchetype(this.archetypeId).survivability.revival);
  }

  /**
   * Arriving at a sanctum — issue #1 phase 4. Guaranteed, one per band
   * boundary, shelled in indestructible seal. Ability cache, healing pool and
   * the weapon altar, in the order you walk past them.
   */
  private updateSanctum(px: number, py: number): void {
    if (!this.worldGen) return;
    const s = sanctumContaining(this.worldGen.sanctums(), px, py);
    if (s === null) {
      this.sanctumPool = null;
      return;
    }
    let v = this.visits.get(s.band);
    if (v === undefined) {
      v = newVisit(s);
      this.visits.set(s.band, v);
      this.raiseAltar(s.altar.x, s.altar.y);
      const id = nextAbility(runAbilities(this.run));
      if (id !== null) {
        this.caches.push({ x: s.cache.x, y: s.cache.y - 1, abilityId: id, claimed: false });
      }
      this.announce('Sanctum', { log: 'a sanctum — the seal holds here' });
    }
    updateVisit(v, px, py);
    this.sanctumPool = v.atPool ? v : null;
    if (v.atPool && this.input.consume('interact', 160)) {
      const frac = drinkPool(v);
      if (frac !== null) {
        const w = this.entities;
        const i = this.playerIdx;
        w.hp[i] = Math.min(w.maxHp[i], w.hp[i] + w.maxHp[i] * frac);
        this.log('the pool is still');
      }
    }
  }

  /**
   * Vendors, polled after the altar so a plinth and a stall can never fight
   * over one interact press (#11). Altars live at sanctums and vendors at rest
   * nodes, so they do not currently coexist — the ordering is defensive.
   */
  private updateVendors(px: number, py: number, dt: number): void {
    this.vendorInRange = null;
    this.vendorView = null;
    for (const v of this.vendors) {
      v.update(dt, px, py, this.run);
      const st = v.status(this.run);
      if (!st.inRange) continue;
      this.vendorInRange = v;
      this.vendorView = {
        factionId: st.faction,
        factionName: st.name,
        standing: st.standing,
        inRange: st.inRange,
        open: st.shopOpen,
        hostile: st.hostile,
        refusal: st.shopOpen ? '' : st.greeting,
        counterId: st.offer?.id ?? null,
        rotateIn: st.timeToRotate,
        offers: v.shelf(this.run).map((e) => ({
          id: e.id,
          label: e.name,
          price: e.price,
          refusal: e.refusal,
        })),
      };
      const ambush = v.takeAmbush(this.run);
      if (ambush) {
        this.log(`${st.name.toLowerCase()} were waiting for you`);
        this.spawnAmbush(ambush);
      }
      if (this.input.consume('interact', 160)) {
        const res = v.buy(this.run);
        this.log(res.message);
        if (res.ok && res.outcome && !this.runModules.includes(res.outcome.moduleId)) {
          this.runModules.push(res.outcome.moduleId);
        }
      }
    }
  }

  /** Fulfil a vendor's ambush request (#11) — world/ owns no entity registry. */
  private spawnAmbush(req: AmbushRequest): void {
    const pool = this.enemies.defs.filter(
      (d) => d.factions.includes(req.faction) && d.rank === 0,
    );
    if (pool.length === 0) return;
    for (let k = 0; k < req.count; k++) {
      const def = pool[this.rng.int(0, pool.length - 1)];
      const ox = req.x + this.rng.int(-30, 30);
      for (let dy = -20; dy <= 20; dy++) {
        const oy = req.y + dy;
        // A real AABB against this body, not a one-column probe. The old test
        // asked whether a single cell was clear and whether one cell below the
        // body's half-height was solid — which cannot know how *wide* the body
        // is, and buried 58.9% of spawned enemies in rock permanently (#56).
        //
        // It was also half a body out of register with the function it called:
        // it treated `oy` as the body's centre while `spawn` treats it as the
        // feet. `bodyFitsAt` uses `spawn`'s convention, so the two now agree.
        if (bodyFitsAt(this.cells, def, ox, oy)) {
          this.spawner.spawn(this.entities, this.cells, def, ox, oy);
          break;
        }
      }
    }
  }

  /**
   * The prologue's beat machine — docs/08 §2, issue #22.
   *
   * Beats advance by *arriving somewhere*, never by a timer: the authored marks
   * in `world/prologue.ts` are the script, and `PROLOGUE_BEAT_IDS` is asserted
   * to match them exactly, so a beat can never be written with nowhere to
   * happen. Progress within a beat is the fade on its own prompt, which the
   * player extinguishes by doing the thing.
   *
   * Skipping is offered only to a profile that has finished it once. Offering
   * to skip onboarding to someone who has never played is offering them the
   * worst option on screen.
   */
  /**
   * Is hold-to-skip on offer right now? — issue #82.
   *
   * One predicate, two input paths. `updatePrologue` reads it to run the hold,
   * and `pollMenuButtons` reads it to decide whether Start is the hold or opens
   * the pause menu; `UIRoot` answers the same question for Escape from
   * `prologueVM.skippable`, which is set from this. Before #82 the keyboard and
   * the pad disagreed — the pad's Start was the hold for the whole prologue
   * while Escape was eaten by the UI's capture-phase listener and never reached
   * `Input` at all — so the hold was operable on exactly one of the two
   * devices. Sharing the fact is what stops that recurring.
   *
   * Gated on `prologueSeen` because a first run is not skippable, and that is
   * also why neither path may simply yield the key for the whole scene: on a
   * first run the button has to keep doing something.
   */
  private get prologueSkipOffered(): boolean {
    return (
      this.scene === 'prologue' &&
      this.prologueBeat !== '' &&
      // Rides in `settings`, which is already a loose string map — no save
      // schema change and therefore no migration for a single boolean.
      this.profile.settings.prologueSeen === true
    );
  }

  private updatePrologue(px: number, py: number, dt: number): void {
    if (this.scene !== 'prologue' || this.prologueBeat === '') return;

    const seenBefore = this.profile.settings.prologueSeen === true;
    if (this.prologueSkipOffered && this.input.held('pause')) {
      this.prologueSkipMs += dt * 1000;
      if (this.prologueSkipMs >= PROLOGUE_SKIP_HOLD_MS) {
        this.endPrologue();
        return;
      }
    } else {
      this.prologueSkipMs = 0;
    }

    const mark = prologueMarkAt(px, py);
    if (mark !== null) {
      const at = PROLOGUE_BEAT_IDS.indexOf(mark.id);
      const now = PROLOGUE_BEAT_IDS.indexOf(this.prologueBeat);
      // Forward only: walking back over an earlier mark must not rewind the
      // script, or the player can loop a lesson they have already learnt.
      if (at > now) this.prologueBeat = mark.id;
    }

    if (this.prologueBeat === PROLOGUE_END_BEAT) {
      this.endPrologue();
      return;
    }
    this.ui.setPrologue(
      prologueVM(this.prologueBeat, 1, seenBefore, this.prologueSkipMs / PROLOGUE_SKIP_HOLD_MS),
    );
  }

  /**
   * Leave the prologue.
   *
   * The strip is the point of the sequence (docs/08 §2), so nothing crosses
   * out of it: the run that follows is built fresh by `main.ts` from the setup
   * screen, and no prologue gear can leak into it because no gear is carried.
   */
  private endPrologue(): void {
    this.prologueBeat = '';
    this.prologueSkipMs = 0;
    this.ui.setPrologue(null);
    this.profile.settings.prologueSeen = true;
    this.persistProfile();
    if (this.onPrologueDone) this.onPrologueDone();
  }

  /** #25: what kind of room a chunk is, for `MapRoomVM.kind`. */
  roomKind(cx: number, cy: number): string {
    return this.worldGen ? this.worldGen.roomKindAt(cx, cy) : 'normal';
  }

  /**
   * Claim an ability cache the player is standing at.
   *
   * The grant lands in three places because three systems own a piece of it:
   * the run state (so it survives to the bank), the mover's bitmask (so it is
   * usable this instant), and the ticker (so the player is told what opened).
   */
  private updateCaches(px: number, py: number): void {
    for (const c of this.caches) {
      if (c.claimed || !inRange(c, px, py)) continue;
      this.cacheInRange = c;
      if (!this.input.consume('interact', 160)) return;
      c.claimed = true;
      this.cacheInRange = null;
      grantAbility(this.run, c.abilityId);
      const bit = ABILITY_BY_ID[c.abilityId];
      if (bit) this.mover.abilities |= bit;
      const t = ABILITY_TEXT[c.abilityId];
      this.announce(`${t.name} found`, {
        log: `found ${t.name.toLowerCase()} — opens ${t.opens}`,
      });
      return;
    }
    this.cacheInRange = null;
  }



  /**
   * Put the biome's boss in the world, once, on first arrival.
   *
   * `spawner.ts` deliberately excludes bosses from the ambient table — "they
   * belong to encounter placement, which is worldgen's business" — and worldgen
   * has no encounter placement, so all twelve Rank.BOSS definitions were
   * unreachable content and Attunement, whose stated source is boss kills, was
   * fed only by elites.
   *
   * The boss is chosen by biome *affinity* from the enemy registry rather than
   * from `BiomeDef.boss`, because eleven of that field's twelve ids name
   * enemies that do not exist — the same id drift that silently zeroed the
   * Lineage track. Affinity is data both sides already maintain.
   */
  private placeBiomeBoss(biomeId: string, px: number, py: number): void {
    const candidates = this.enemies.defs.filter(
      (d) => d.rank === 2 && d.biomes.includes(biomeId as BiomeId),
    );
    if (candidates.length === 0) return;
    const def = candidates[this.rng.int(0, candidates.length - 1)];

    // Far enough that arriving in a biome is not an ambush, near enough that
    // it is the thing you are walking toward.
    const dir = this.rng.int(0, 1) === 0 ? 1 : -1;
    const dist = 220 + this.rng.int(0, 120);
    const bx = Math.round(px + dir * dist);
    for (let dy = -80; dy <= 200; dy++) {
      const by = Math.round(py) + dy;
      // Same AABB test as the ambush path, and it matters more here: a boss is
      // the widest body in the roster, so the one-column probe this replaces
      // embedded 100% of bodies at width >= 21 (#56).
      if (bodyFitsAt(this.cells, def, bx, by)) {
        if (this.spawner.spawn(this.entities, this.cells, def, bx, by) !== -1) {
          this.log(`something large stirs in the ${biomeId.replace(/_/g, ' ')}`);
        }
        return;
      }
    }
  }

  private render(dt: number): void {
    const gl = this.glc.gl;
    const w = this.entities;
    const i = this.playerIdx;

    this.camera.update(
      w.x[i],
      w.y[i],
      w.vx[i],
      w.vy[i],
      this.glc.width / this.glc.height,
      dt,
    );

    gl.viewport(0, 0, this.glc.drawWidth, this.glc.drawHeight);
    gl.clearColor(0.027, 0.027, 0.043, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);

    // Ambient is a property of depth, not a constant — issue #31. Read at the
    // camera rather than at the player so the value does not snap when the
    // player crosses a band line off-screen.
    this.lightTex.ambient = this.scene === 'prologue'
      ? PROLOGUE_AMBIENT
      : ambientAt(this.camera.renderY);
    this.lightTex.update(this.light, this.camera);
    // Layer 1 of docs/01 §6.2. After `lightTex.update`, because it reads the
    // same ambient and incident terms the terrain does and a background lit
    // differently from the rock in front of it reads as a compositing error.
    // Before the terrain pass, because VOID discards — this is what shows
    // through. `worldGen` is null in the prologue, which it takes as "no sky".
    //
    // The fourth argument is the day/night cycle (#54), and `this.cells.tick`
    // is deliberately the only thing it reads: not `this.time`, which is a
    // wall-clock accumulator, and not `dt`. The tick advances once per
    // `stepSimulation` and not at all while paused, so the sky is a function of
    // simulated time on every machine at every frame rate. It is cosmetic —
    // see `src/render/dayNight.ts`; nothing downstream of this line reads it.
    this.backdrop.render(this.camera, this.lightTex, this.worldGen, dayPhase(this.cells.tick));
    // Fifth argument is the rigid solver, as a `GrainSource` (#121): a placed
    // body's pixels take their speckle from the body's own window instead of
    // from the world hash, so the grain turns with a rotating crate. Optional
    // in the same way and for the same reason `light` is -- omitted, the shader
    // runs at uBodyCount 0 and the image is bit-for-bit the pre-#121 one.
    this.terrain.render(this.cells, this.camera, this.time, this.lightTex, this.rigid);

    // Particles below entities: a spray should read as passing behind the
    // character, not obscuring them.
    this.sprites.begin(false);
    this.sprites.pushParticles(this.particles);
    this.sprites.flush(this.camera);

    this.drawAltar();

    // The body poses to the aim. `WielderState.aim` is already in
    // `poseSkeleton`'s frame — 0 forward, ±PI/2 vertical, NaN neutral — and
    // only the player has one, so only the player is told.
    //
    // Without this line the aim posing is dormant and the game is bit-identical
    // to before it existed, which is what the "a neutral aim leaves the body
    // exactly as it was" test guards. We shipped 8-directional aiming and the
    // body did not follow it; this is what connects them.
    this.bodies.noteAim(this.playerIdx, this.wielder.aim);
    this.bodies.update(w, dt);
    this.bodies.applyEnvironment(w, this.cells, this.cells.tick);
    this.drawBodies();

    this.sprites.begin(false);
    this.drawEntities();
    this.drawProjectiles();
    this.sprites.flush(this.camera);

    // Weapons draw after bodies so a swing passes in front of the wielder.
    this.drawWeapons();

    // Hit flash decays on the render clock; it is pure presentation.
    w.each(Comp.SPRITE, (k) => {
      if (w.flashTime[k] > 0) w.flashTime[k] = Math.max(0, w.flashTime[k] - dt);
    });

    if (this.craftDirty) {
      this.craftDirty = false;
      // `seenMaterials()` is the codex record — a recipe naming a material this
      // profile has never touched is withheld rather than shown unaffordable.
      this.craftingView = craftingVM(
        this.run, this.progression, this.uiAdapter.seenMaterials(), this.atRestNode,
      );
    }
    profiler.begin('ui');
    // #134 -- the HUD's key caps resolve through the table the game is actually
    // being played on, not through `DEFAULT_BINDINGS`. This is the seam: the
    // overrides live on the profile, `main.ts` lays them over this `Input`
    // (`Shell.applyBindings`), and `Game` is the only object holding both that
    // `Input` and the adapter. The serial makes this a comparison rather than a
    // rebuild on every frame that changed nothing, which is all of them.
    this.uiAdapter.setBindings(this.input.bindingTable(), this.input.bindingSerial());
    this.ui.update(
      this.uiAdapter.update(w, i, this.mover, this.wielder, this.paused, {
        run: this.run,
        owned: { weapons: this.runWeapons, modules: this.runModules },
        biomeHazard: this.biomeHazard,
        atRestNode: this.atRestNode,
        roomKind: this.roomKindFn,
        vendor: this.vendorView,
        stash: this.stashView,
        crafting: this.craftingView,
        // #129. A field-for-field copy off the hold state and the field, with
        // nothing derived on the way -- the adapter turns the cell distance into
        // a fraction of the radius, because the radius is on that side too.
        gather: {
          active: this.gather.active,
          tier: this.gather.tier,
          radius: this.attract.radius,
          hazardDist: this.attract.hazardDist,
          hazardMat: this.attract.hazardMat,
          hazardCount: this.attract.hazardCount,
        },
      }),
      dt,
    );
    profiler.end('ui');
  }

  /**
   * Characters are pixel bodies (see entity/pixelBody.ts). Anything without a
   * body — because the atlas is full — still draws as a rimmed rectangle, so
   * running out of slots degrades instead of making entities invisible.
   */
  private drawBodies(): void {
    const w = this.entities;
    const r = this.bodyRenderer;
    r.begin();

    this.bodies.each((i, e) => {
      if (w.alive[i] !== 1) return;
      if (e.body.dirty) r.uploadSlot(e.slot, e.body);

      const flash = w.flashTime[i] > 0;
      const iframe = w.iframes[i] > 0 && ((this.time * 30) | 0) % 2 === 0;

      // Sizing lives in `bodyQuad` because it used to live here *and* in
      // `rasterize`, and the two copies multiplied — every body drew
      // `plan.height * scale²` tall. One raster texel is now one world unit, so
      // the quad is simply the box and drawn height is `2 * halfH` by
      // construction. See the note on `bodyQuad`.
      // #98: the STANDING height. `bodyQuad`'s scale is the sole plan-to-pixel
      // factor, so feeding it a crouched `halfH` would shrink the figure to 57%
      // on top of the fold rather than instead of it. `cy` below still reads
      // the live `halfH`, because that is the feet and the feet do not move.
      const q = bodyQuad(standingHalfH(w, i), e.body.plan);
      const cy = w.y[i] + w.halfH[i] - q.feetOffset;

      // The AI's state, drawn. Enemies now announce the envelope they commit
      // to (`behaviors.ts` -> `noteAttack`), and this is what makes that
      // announcement legible: a *timed* tell that opens on the commit frame and
      // rises across the wind-up, rather than a binary "is attacking" flag.
      // Returns a shared object — read it before the next call, the way
      // `swingPose` is. `glowOf` is folded in.
      const tell = this.bodies.telegraph(w, i);
      const hit = flash ? 2.4 : 1;

      r.push(
        w.x[i], cy, q.halfWidth, q.halfHeight,
        e.slot, w.facing[i] < 0,
        hit * tell.r, hit * tell.g, hit * tell.b,
        iframe ? 0.55 : 1,
        tell.glow,
      );
    });

    r.flush(this.camera, this.lightTex);
  }

  /**
   * Pedestal markers: a floating rune per offer, brighter when focused.
   * Deliberately drawn additively — the altar should read as the one lit thing
   * in a dark spawn room.
   */
  private drawAltar(): void {
    if (!this.altar) return;
    const st = this.altar.status();
    if (!st.offer) return;
    const o = st.offer;
    const b = this.sprites;
    const bob = Math.sin(this.time * 2.0) * 1.4;
    const y = o.y - 20 + bob;

    // --- the weapon itself, drawn in-world as an object ---
    // Same geometry the swing uses, held at rest, so what is on the plinth is
    // unmistakably the thing you will be holding.
    const v = this.visualFor(this.playerIdx, o.resolved);
    b.begin(false);
    if (v) {
      const ang = -0.62; // presented tilted, hilt low
      const segs = Math.max(1, v.segments);
      // Normalise the display length: a whip and a dagger must both sit on the
      // plinth legibly. True scale is for the swing, where the difference is
      // the point; here the job is identification.
      const shown = Math.min(v.length, 26);
      const segLen = shown / segs;
      let px = o.x - Math.cos(ang) * shown * 0.5;
      let py = y - Math.sin(ang) * shown * 0.5;
      let a = ang;
      const pr = M.palette[v.mat * 4] / 255;
      const pg = M.palette[v.mat * 4 + 1] / 255;
      const pb = M.palette[v.mat * 4 + 2] / 255;
      const emis = M.emissive[v.mat];
      // A whip coils rather than lying straight, so it still reads as a whip.
      const curl = segs > 1 ? 0.42 : 0;
      for (let k = 0; k < segs; k++) {
        const nx = px + Math.cos(a) * segLen;
        const ny = py + Math.sin(a) * segLen;
        const steps = Math.max(1, Math.ceil(segLen / 1.6));
        for (let st2 = 0; st2 <= steps; st2++) {
          const t = st2 / steps;
          const r = v.thickness * profileAt(v.profile, (k + t) / segs);
          b.push(
            px + (nx - px) * t, py + (ny - py) * t,
            Math.max(0.6, r), Math.max(0.6, r),
            pr + emis * 0.5, pg + emis * 0.35, pb + emis * 0.2, 1,
            Shape.RECT,
          );
        }
        px = nx;
        py = ny;
        a += curl;
      }
    }
    b.flush(this.camera);

    // --- the light column, additive, so the plinth reads across a room ---
    b.begin(true);
    const on = st.inRange;
    b.push(o.x, y, on ? 9 : 6.5, on ? 9 : 6.5, 1, 0.78, 0.4, on ? 0.5 : 0.28, Shape.BLOB);
    b.push(o.x, o.y - 10, 1.0, 10, 1, 0.78, 0.4, on ? 0.35 : 0.16, Shape.RECT);
    b.flush(this.camera);
  }

  /**
   * Weapons, drawn in world space rather than into the body atlas.
   *
   * They are routinely longer than the body raster box — a whip is ~64 cells —
   * so rasterising them with the character silently clipped them to its own
   * footprint. Here they are unbounded, and a whip can legitimately reach three
   * body-lengths the way Castlevania's does.
   */
  /**
   * The two acquisition verbs, so there is no site to forget (#107).
   *
   * `addWeapon`/`addModule` were exported, tested and called from nowhere, so
   * the death screen's items list was empty for every run ever played. Six
   * paths acquire: kit issue, altar claim, kill drop, stash withdrawal, vendor
   * purchase and craft. The last two own a `RunState` already and record inside
   * their own transaction; these are the other four.
   *
   * The asymmetry is deliberate: `addWeapon` keys on a unique instance id, so
   * the ledger is a history of pickups, while modules dedupe. Re-socketing a
   * module is a movement, not an acquisition, and does not come through here.
   */
  private acquireWeapon(inst: WeaponInstance): void {
    this.runWeapons.push(inst);
    this.recordWeapon(inst);
    for (const id of inst.modules) this.acquireModule(id);
  }

  private acquireModule(id: string): void {
    if (!this.runModules.includes(id)) this.runModules.push(id);
    this.recordModule(id);
  }

  /**
   * The two ledger writes, separated from the two acquisition verbs because
   * **the constructor issues the kit before the run exists** -- `equipKit` runs
   * at the loadout step and `this.run` is not assigned until the ecology step
   * some 240 lines later. Writing straight through threw on `run.inventory`
   * and took the game down on boot.
   *
   * So a pre-run acquisition holds its entry in `runWeapons`/`runModules` and
   * `adoptHoldings` banks it the moment the run exists. Dropping the write
   * would have been the smaller diff and the wrong one: the kit is the run's
   * frame-one loadout, so silently omitting it is #107 reopened for the one
   * acquisition every run makes.
   */
  private recordWeapon(inst: WeaponInstance): void {
    if (this.run === undefined) return;
    if (!this.run.inventory.weapons.includes(inst.id)) addWeapon(this.run, inst.id);
  }

  private recordModule(id: string): void {
    if (this.run === undefined) return;
    if (!this.run.inventory.modules.includes(id)) addModule(this.run, id);
  }

  /** Bank whatever was acquired before `this.run` existed. Idempotent. */
  private adoptHoldings(): void {
    for (const inst of this.runWeapons) this.recordWeapon(inst);
    for (const id of this.runModules) this.recordModule(id);
  }

  private drawWeapons(): void {
    const w = this.entities;
    const b = this.sprites;
    b.begin(false);

    this.bodies.each((i, e) => {
      if (w.alive[i] !== 1) return;
      // The tether is the player's, and it is drawn whether or not a swing is in
      // flight — hanging from a rope is not an attack.
      const tethered = (w.mask[i] & Comp.PLAYER) !== 0 && this.mover.tetherActive;
      if (!tethered && (!e.swinging || !e.visual)) return;
      // `bodyQuad`, not a re-derivation. This line used to compute the scale
      // factor itself — an exact copy of the number extracted in step 1, and
      // copies of that particular number are what made every body draw at
      // `plan.height * scale²` in the first place.
      const { scale } = bodyQuad(standingHalfH(w, i), e.body.plan);
      const face = w.facing[i] < 0 ? -1 : 1;

      // Body-local (hip-rooted, authored facing right) → world.
      //
      // The pivot is the hand unless the style displaced it (#88). Rotary, nova
      // and halo turn about the body, not the grip, and were inexpressible
      // while this line could only read `handX`/`handY`. `poseOne` has already
      // resolved `SwingPose`'s height fractions into body-local units, so both
      // branches are in the same frame and the facing mirror applies to either
      // without a second case.
      //
      // A tether always hangs from the grip: a rope tied to the centre of the
      // torso reads as impalement.
      const bodyPivot = e.swingBodyPivot && !tethered;
      const pivotX = bodyPivot ? e.swingPivotX : e.handX;
      const pivotY = bodyPivot ? e.swingPivotY : e.handY;
      const originX = w.x[i] + pivotX * scale * face;
      const originY = w.y[i] + w.halfH[i] + pivotY * scale;

      // --- the rope, hand to anchor ------------------------------------------
      //
      // Drawn before the weapon and outside the `e.visual` gate, because a
      // player tethering with a pistol equipped still has a rope. `tetherRope`
      // owns the geometry — see `abilities.ts` for why the far end is the anchor
      // cell rather than a `tetherLen` ray, and `tests/tetherRope.test.ts` for
      // the pixel it is held to.
      if (tethered) {
        const rn = tetherRope(this.mover, originX, originY, TETHER_ROPE_SEGMENTS, this.ropeScratch);
        const rr = M.palette[MAT.STEEL * 4] / 255;
        const rg = M.palette[MAT.STEEL * 4 + 1] / 255;
        const rb = M.palette[MAT.STEEL * 4 + 2] / 255;
        for (let k = 0; k + 1 < rn; k++) {
          const ax = this.ropeScratch[k * 2];
          const ay = this.ropeScratch[k * 2 + 1];
          const bx = this.ropeScratch[k * 2 + 2];
          const by = this.ropeScratch[k * 2 + 3];
          const steps = stampSteps(Math.hypot(bx - ax, by - ay));
          for (let st = 0; st <= steps; st++) {
            const t = st / steps;
            b.push(ax + (bx - ax) * t, ay + (by - ay) * t, 0.7, 0.7, rr, rg, rb, 1, Shape.RECT);
          }
        }
      }

      const v = e.visual;
      if (!v || v.style === 'none') return;

      // Follow the aim, then mirror the swing with the character.
      //
      // `swingPose` animates a *style* against a progress value and has no
      // wielder to ask where the player was pointing, so the rotation is added
      // here — from the same per-swing latch the hitbox used, so the blade and
      // the thing it cuts cannot drift apart. Without this the sword draws level
      // while the hitbox carves the ceiling, which reads as the dig being
      // broken rather than as the drawing being wrong.
      //
      // While the rope is taut the weapon points at the anchor instead. That is
      // the other half of #116: the swing pose is a forward crack and the anchor
      // is usually above you, so drawing the pose during a tether puts the two
      // ends of one object at a right angle to each other.
      const aimRot = (w.mask[i] & Comp.PLAYER) !== 0 ? swingAimRotation(this.wielder) : 0;
      const ang = tethered
        ? tetherDrawAngle(this.mover, originX, originY)
        : drawnSwingAngle(e.swingAngle, aimRot, face);

      const segs = Math.max(1, v.segments);
      // A tethered weapon is held out along the rope at full reach rather than
      // frozen at whatever `swingExtend` the last attack left behind.
      const extend = e.swinging ? e.swingExtend : 1;
      const total = v.length * scale * extend;
      const curl = tethered ? 0 : face < 0 ? -e.swingCurl : e.swingCurl;

      const pr = M.palette[v.mat * 4] / 255;
      const pg = M.palette[v.mat * 4 + 1] / 255;
      const pb = M.palette[v.mat * 4 + 2] / 255;
      const emis = M.emissive[v.mat];

      // `swingSpine` is this loop's own joint walk, lifted into `wielder.ts` so
      // `tests/drawnAim.test.ts` measures the tip angle the renderer produces
      // rather than a copy of the arithmetic that produces it — which is exactly
      // how #112's rotation and `fireOrSwing`'s came to mean different things.
      const sn = swingSpine(originX, originY, ang, curl, total, segs, this.spineScratch);
      for (let k = 0; k + 1 < sn; k++) {
        const px = this.spineScratch[k * 2];
        const py = this.spineScratch[k * 2 + 1];
        const nx = this.spineScratch[k * 2 + 2];
        const ny = this.spineScratch[k * 2 + 3];
        const segLen = Math.hypot(nx - px, ny - py);
        const steps = stampSteps(segLen);
        for (let st = 0; st <= steps; st++) {
          const t = st / steps;
          // The weapon's own outline, not a taper. `profileAt` returns a
          // multiple of `thickness`, so this is the same expression it always
          // was with the constant replaced by the class's profile — #113.
          // `MIN_HALF_WIDTH` is the clamp `tests/weaponSilhouette.test.ts`
          // measures silhouettes against; keep the two the same number.
          const stamp = this.weaponStamp;
          stampAt(v.profile, v.thickness, scale, face, (k + t) / segs, px, py, nx, ny, t, stamp);
          this.sprites.push(
            stamp.x, stamp.y, stamp.r, stamp.r,
            pr + emis * WEAPON_GLOW.r, pg + emis * WEAPON_GLOW.g, pb + emis * WEAPON_GLOW.b, 1,
            Shape.RECT,
          );
        }
      }
    });

    b.flush(this.camera);
  }

  /**
   * Rounds in flight — #85.
   *
   * Coloured by damage type from the same table the blade materials use, so a
   * fire round and a fire sword read as the same element without anyone
   * authoring a second palette.
   *
   * **Shaped, and pointed the way it is going.** This was one `Shape.CIRCLE` at
   * `max(1.2, p.radius[k])` — one primitive and one scalar for the whole
   * projectile catalogue, with `rot` left at zero on an object whose defining
   * property is a velocity. Ten of the fifty-five class pairs drew the identical
   * pixel. `entity/weaponVisual.ts`'s rounds section owns the vocabulary and
   * `tests/projectileSilhouette.test.ts` is the before/after.
   *
   * The run is drawn *behind* the nose and its length is the distance
   * `Projectiles.step` actually swept and resolved hits along this step. Eight
   * of eleven classes were drawn shorter than that; the rail was drawn at 11% of
   * it. At a run of one diameter this is exactly the circle it replaces.
   *
   * **The rim is dropped, and that is a bug fix.** The old call passed
   * `rimW = 1.0`; the fragment shader's circle branch reads `rimW` as a
   * *fraction of the radius* while the rect branch reads it as *world units*, so
   * at 1.0 the whole disc was rim and every round in the game drew as a
   * near-black dot with its colour computed and thrown away.
   */
  private drawProjectiles(): void {
    const p = this.projectiles;
    const b = this.sprites;
    for (let k = 0; k < p.count; k++) {
      const mat = bladeMatForDamage(p.damageType[k]);
      const pr = M.palette[mat * 4] / 255;
      const pg = M.palette[mat * 4 + 1] / 255;
      const pb = M.palette[mat * 4 + 2] / 255;
      const emis = M.emissive[mat];
      const r = Math.max(MIN_ROUND_RADIUS, p.radius[k]);
      const vx = p.vx[k];
      const vy = p.vy[k];
      const speed = Math.hypot(vx, vy);
      const ux = speed > 0 ? vx / speed : 1;
      const uy = speed > 0 ? vy / speed : 0;
      // `SIM_DT` and not the render `dt`: the run is the distance the *sim*
      // swept, so a frame-rate dip must not stretch the round.
      const run = roundRunLength(speed, r, SIM_DT);
      const n = roundStamps(run);
      const half = run / (2 * n);
      const rot = Math.atan2(uy, ux);
      const nx = p.x[k] + ux * r;
      const ny = p.y[k] + uy * r;
      const profile = p.profile[k];
      for (let s2 = 0; s2 < n; s2++) {
        const along = (s2 + 0.5) / n;
        const back = run * (1 - along);
        b.push(
          nx - ux * back, ny - uy * back,
          half, Math.max(MIN_HALF_WIDTH, r * roundProfileAt(profile, along)),
          pr + emis * WEAPON_GLOW.r, pg + emis * WEAPON_GLOW.g, pb + emis * WEAPON_GLOW.b, 1,
          Shape.RECT, 0, 0, 0, 0, rot,
        );
      }
    }
  }

  private drawEntities(): void {
    const w = this.entities;
    const b = this.sprites;

    w.each(Comp.TRANSFORM | Comp.SPRITE, (i) => {
      if (this.bodies.has(i)) return; // drawn as a pixel body instead
      const isPlayer = (w.mask[i] & Comp.PLAYER) !== 0;
      const hw = w.halfW[i] * w.scaleX[i];
      const hh = w.halfH[i] * w.scaleY[i];

      if (isPlayer) {
        // The rim is a shipping requirement, not decoration: it is what
        // guarantees the silhouette survives a screen full of fire.
        b.push(
          w.x[i], w.y[i], hw, hh,
          0.85, 0.87, 0.92, 1,
          Shape.RECT,
          0.05, 0.05, 0.07, 1.0,
        );
        // Facing pip, so which way you're aiming is readable at 1x zoom.
        b.push(
          w.x[i] + w.facing[i] * (hw + 1.5), w.y[i] - hh * 0.35,
          1.5, 1.5,
          1, 0.85, 0.3, 1,
          Shape.RECT,
        );
      } else {
        // Enemies are drawn in the colour of what they are made of. The
        // premise of the ecology is that a body is a pile of materials with
        // derived weaknesses (docs/03 §6) — if a fungal beast and a steel
        // knight are both "an enemy-coloured rectangle", the player has no way
        // to read the weakness they are supposed to be exploiting.
        const core = w.coreMat[i];
        const pr = M.palette[core * 4] / 255;
        const pg = M.palette[core * 4 + 1] / 255;
        const pb = M.palette[core * 4 + 2] / 255;
        const flash = w.flashTime[i] > 0 ? 1 : 0;
        const emis = M.emissive[core];
        b.push(
          w.x[i], w.y[i], hw, hh,
          flash ? 1 : pr + emis * 0.4,
          flash ? 1 : pg + emis * 0.25,
          flash ? 1 : pb + emis * 0.1,
          1,
          Shape.RECT,
          // Hostile rim: reserved chroma, so an enemy silhouette never reads as
          // terrain no matter what it is built from.
          0.75, 0.18, 0.16, 1,
        );
      }
    });
  }

  destroy(): void {
    this.stop();
    this.input.destroy();
    // The UI holds window-level key capture. Leaving it alive after the game is
    // torn down means a dead `UIRoot` keeps eating Esc/Tab/C/M on whatever
    // screen comes next — which is exactly what quitting to the title does.
    // The shell used to work around this with an ownership flag; owning the
    // teardown here is the fix that lets the workaround go.
    this.ui.destroy();
  }
}
